'use strict';

// external imports
import React, {
    useCallback,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import _ from 'lodash';
import {v4 as uuidV4} from 'uuid';

// prime react imports
import {Button} from 'primereact/button';
import {
    Checkbox,
    CheckboxChangeEvent
} from 'primereact/checkbox';
import {ChevronDownIcon} from 'primereact/icons/chevrondown';
import {ChevronRightIcon} from 'primereact/icons/chevronright';
import {ContextMenu} from 'primereact/contextmenu';
import {
    MenuItem,
    MenuItemCommandEvent
} from 'primereact/menuitem';
import {Panel} from 'primereact/panel';
import {Toolbar} from 'primereact/toolbar';
import {Tooltip} from 'primereact/tooltip';
import {
    Tree,
    TreeEventNodeEvent,
    TreeSelectionEvent,
    TreeMultipleSelectionKeys
} from 'primereact/tree';
import TreeNode from 'primereact/treenode';
import {TreeTableExpandedKeysType} from 'primereact/treetable';
import {IconUtils} from 'primereact/utils';

// shared imports
import messageCatalog, {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item, {
    ItemAttributesForCreation
} from '~/shared/src/ci/Item';
import Reference, {
    ReferenceAttributesForCreation
} from '~/shared/src/ci/Reference';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';
import dataExtraction from '~/shared/src/utilities/dataExtraction';
import {
    fieldTypeToItemTypeMapping,
    LoadContainerData,
    LoadTabContainerData
} from '~/shared/src/utilities/modules/form';
import {
    aliasSeparator,
    buildDataDescriptor,
    DataDescriptor,
    DataObjectProperties,
    DataStructure,
    extractDataObjectsFromDataStructure,
    getDataObject,
    getParentDataObject,
    getReferenceSetsForDataObject
} from '~/shared/src/utilities/dataStructure';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {LoadItemResult} from '~/shared/src/utilities/modules/data';
import {LoadedItemData} from '~/shared/src/utilities/loadedData';

// frontend components import
import ContentButton, {
    ReferenceType as ContentButtonType
} from '~/frontend/src/components/base/shared/ContentButton';
import getAddItemDialog from '~/frontend/src/components/base/shared/tree/ItemTree/AddTreeNodePanel';

// frontend imports
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {
    DragData,
    DragOptions,
    ReferenceType as FormDesignerReferenceType
} from '~/frontend/src/components/base/FormDesigner';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {
    getIconSpan,
    getItemKeyDisplayName,
    getItemNameDisplayName,
    resolveLigatureIcon
} from '~/frontend/src/utilities/renderHelpers';
import {getPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {
    clearElusiveData,
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import addItem from '~/frontend/src/actions/data/addItem';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import deleteReference from '~/frontend/src/actions/data/deleteReference';
import updateReference from '~/frontend/src/actions/data/updateReference';
import setPanelProps from '~/frontend/src/actions/panelManager/setPanelProps';
import xhr from '~/frontend/src/utilities/xhr';


type DataConfig = {
    itemTypeName : string;
    key : string;
    name : string;
    dataObjectId? : number;
    isRepeated? : boolean;
    properties? : {
        [ propertyTypeName : string ] : AttributeValue;
    };
    enumProperties? : {
        [ propertyTypeName : string ] : {
            key? : string;
            value? : AttributeValue;
        }[]
    };
    additionalData? : ( DataConfig & {
        referenceExisting? : boolean;
        item? : LoadedItemData;
        referenceTypeName : string;
        referenceDirection : 'src' | 'dest';
    } )[];
};

type LayoutElementDefinition = {
    type : 'item' | 'property' | 'image' | 'meta' | 'reference' | 'referenceProperty' | 'referenceImage' | 'referenceMeta' | 'referenceField' | 'fileField';
    key : string;
    label : string;
    icon : string;
    dataDescriptorAlias? : string;
    data : DataConfig;
};

type ContainerData = LoadContainerData | LoadTabContainerData;
type ContainerStructure = ContainerData & {
    children : ContainerStructure[];
};

type ExtendTreeNode = TreeNode.TreeNode & {
    key : string;
    label : string;
    tooltip? : React.ReactNode;
    dataObject? : DataObjectProperties;
    children? : ExtendTreeNode[];
    data : {
        layoutElementDefinition : LayoutElementDefinition[];

        buttons? : React.ReactNode[];
        dataDescriptorAlias? : string[];
    };
}

export type DefaultProps = {

    readOnly? : boolean;

    // props to be passed to ItemTree
    expandedKeys? : TreeTableExpandedKeysType;

    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    scrollTop? : number;
    scrollLeft? : number;

    newLayoutElementsWidth? : string;

    showTypeLabels? : boolean;
    showReferenceFields? : boolean;
    showMetaFields? : boolean;
};
export const defaultProps : Required<DefaultProps> = {
    readOnly                     : false,
    expandedKeys                 : {},
    additionalElusiveDataContexts: [],
    scrollTop                    : 0,
    scrollLeft                   : 0,
    newLayoutElementsWidth       : '100%',
    showTypeLabels               : false,
    showReferenceFields          : false,
    showMetaFields               : false
};

export type Props = {
    uid : string;
    parentUid : string;

    dataContext : string;

    rootItemId : number;

    onNewLayoutElementDragStart? : FormDesignerReferenceType['startDrag'];

    onMarkLayoutElementsForAlias? : ( alias : string | undefined ) => void;

    selectionKeys? : TreeMultipleSelectionKeys;

    selectedLayoutElementId? : number;

    repeatedDataObjects? : number[];

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectLayoutElement? : ( id? : number ) => void;

    nodeIdToPanelKeyMap? : {
        [ nodeId : string ] : string
    };
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ReferenceType = {
    markFieldsForAlias : ( alias : string | undefined ) => void;
};

type ElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    dataStructure? : DataStructure;
    staticTreeNodes? : TreeNode.TreeNode[];
    data? : {
        [ dataDescriptorAlias : string ] : DataObjectProperties;
    };
    treeData? : ExtendTreeNode[];
    scrollPositions? : {
        [ treeNodeDataKey : string ] : number;
    }
};


const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;
const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;


const DesignerTree = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const dispatch : AppDispatch                     = useDispatch(),
          {
              additionalElusiveDataContexts,
              dataContext,
              expandedKeys,
              newLayoutElementsWidth,
              nodeIdToPanelKeyMap,
              onMarkLayoutElementsForAlias,
              onNewLayoutElementDragStart,
              onSelectLayoutElement,
              parentUid,
              readOnly,
              repeatedDataObjects,
              rootItemId,
              scrollLeft,
              scrollTop,
              selectedLayoutElementId,
              selectionKeys,
              showMetaFields,
              showReferenceFields,
              showTypeLabels,
              uid
          }                                          = props,
          user                                       = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp                    = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          metaModelTimestamp                         = useSelector( ( state : StoreState ) => state.metamodel?.timestamp ? state.metamodel.timestamp : 0 ),
          elusiveData                                = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          allElusiveData                             = useSelector( ( state : StoreState ) => state.elusiveData ),
          lastParams                                 = useRef<any>( null ),
          initialRendered                            = useRef<boolean>( false ),
          treeElement                                = useRef<HTMLDivElement | null>( null ),
          contextMenu                                = useRef<ContextMenu | null>( null ),
          nodeKeyToDataDescriptorAliasesRepresenting = useRef<{ [ nodeKey : string ] : string[] }>( {} ),
          nodeButtons                                = useRef<{
              [ buttonId : string ] : { justInteracted : boolean, content? : ContentButtonType }
          }>( {} ),
          ignoreSelectionChange                      = useRef<boolean>( false ),
          [ contextMenuNode, setContextMenuNode ]    = useState<string | undefined>( undefined ),
          [ aliasToMark, setAliasToMark ]            = useState<string | undefined>( undefined ),
          language                                   = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          globalDataVersions                         = useMemo(
              () => {
                  return JSON.stringify( allElusiveData?.[ dataContext ]?.data ?? {} );
              },
              [
                  allElusiveData?.[ dataContext ]?.data
              ]
          ),
          // TODO: external config
          staticTreeNodes : TreeNode.TreeNode[]      = useMemo(
              () => {
                  void ( async () => {
                      const response = await xhr.post(
                          `${BASE_URL}/api/data/loadItem`,
                          {
                              schema      : 'CFG',
                              itemTypeName: 'Message',
                              key         : 'Common:StaticContentField:Separator'
                          }
                      );

                      if ( response?.data ) {
                          const responseData = response.data as LoadItemResult;

                          if ( responseData.success ) {
                              staticTreeNodes[ 0 ].data.layoutElementDefinition[ 8 ].data.additionalData[ 0 ].item = responseData.item;
                          }
                      }
                  } )();

                  return [
                      // static
                      {
                          id      : 'static',
                          key     : 'static',
                          label   : __( 'YN.base.frontend.formDesigner.treeTable.static' ),
                          icon    : 'yn-tree-icon fa fa-cube',
                          children: [],
                          data    : {
                              layoutElementDefinition: [

                                  // single container
                                  {
                                      key  : 'Container',
                                      label: __( 'YN.base.frontend.formDesigner.static.container' ),
                                      icon : 'yn-icon  fa-regular fa-rectangle-list',
                                      data : {
                                          itemTypeName  : 'Container',
                                          key           : 'Container',
                                          name          : 'Container',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Title',
                                                  name              : 'Title',
                                                  referenceTypeName : 'isTitleFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'DE', undefined, undefined, undefined, undefined, true )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'EN', undefined, undefined, undefined, undefined, true )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },

                                  // CalendarView
                                  {
                                      key  : 'CalendarView',
                                      label: __( 'YN.base.frontend.formDesigner.static.calendarView' ),
                                      icon : 'yn-icon fas fa-calendar-days',
                                      data : {
                                          itemTypeName: 'CalendarView',
                                          key         : 'CalendarView',
                                          name        : 'CalendarView'
                                      }
                                  },

                                  // ChartView
                                  {
                                      key  : 'ChartView',
                                      label: __( 'YN.base.frontend.formDesigner.static.chartView' ),
                                      icon : 'yn-icon fas fa-chart-line',
                                      data : {
                                          itemTypeName: 'ChartView',
                                          key         : 'ChartView',
                                          name        : 'ChartView'
                                      }
                                  },

                                  // OverviewView
                                  {
                                      key  : 'OverviewView',
                                      label: __( 'YN.base.frontend.formDesigner.static.overviewView' ),
                                      icon : 'yn-icon fas fa-table',
                                      data : {
                                          itemTypeName: 'OverviewView',
                                          key         : 'OverviewView',
                                          name        : 'OverviewView'
                                      }
                                  },

                                  // StaticContentField
                                  {
                                      key  : 'StaticContentField',
                                      label: __( 'YN.base.frontend.formDesigner.static.staticContentField' ),
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StaticContentField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'StaticContentField',
                                          key           : 'StaticContentField',
                                          name          : 'StaticContentField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Style',
                                                  key               : 'FieldLike',
                                                  name              : 'FieldLike',
                                                  referenceTypeName : 'isStyleFor',
                                                  referenceDirection: 'src',
                                                  enumProperties    : {
                                                      ClassName: [
                                                          {
                                                              key: 'yn-fieldlike'
                                                          }
                                                      ]
                                                  }
                                              },
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.staticContentField.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.staticContentField.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },

                                  // Button
                                  {
                                      key  : 'Button',
                                      label: __( 'YN.base.frontend.formDesigner.static.button' ),
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'ButtonField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'ButtonField',
                                          key           : 'Button',
                                          name          : 'Button',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.button.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.button.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              },
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'ButtonLabel',
                                                  name              : 'ButtonLabel',
                                                  referenceTypeName : 'isButtonLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.button.buttonLabel', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.button.buttonLabel', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },

                                  // Button without label
                                  {
                                      key  : 'ButtonWithLabel',
                                      label: __( 'YN.base.frontend.formDesigner.static.buttonWithoutLabel' ),
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'ButtonField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'ButtonField',
                                          key           : 'Button',
                                          name          : 'Button',
                                          properties    : {
                                              LabelVisibility: 'hidden',
                                              InfoTextLayout : 'hidden'
                                          },
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'ButtonLabel',
                                                  name              : 'ButtonLabel',
                                                  referenceTypeName : 'isButtonLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.button.buttonLabel', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.button.buttonLabel', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },

                                  // placeholder
                                  {
                                      key  : 'Placeholder',
                                      label: __( 'YN.base.frontend.formDesigner.static.placeholder' ),
                                      icon : 'yn-icon  fas fa-expand',
                                      data : {
                                          itemTypeName: 'StaticContentField',
                                          key         : 'StaticContentField:Placeholder',
                                          name        : 'StaticContentField:Placeholder',
                                          properties  : {
                                              LabelVisibility: 'hidden',
                                              InfoTextLayout : 'hidden'
                                          }
                                      }
                                  },

                                  // Separator
                                  {
                                      key  : 'Separator',
                                      label: __( 'YN.base.frontend.formDesigner.static.separator' ),
                                      icon : 'yn-icon fas fa-minus',
                                      data : {
                                          itemTypeName  : 'StaticContentField',
                                          key           : 'StaticContentField:Separator',
                                          name          : 'StaticContentField:Separator',
                                          properties    : {
                                              LabelVisibility: 'hidden',
                                              InfoTextLayout : 'hidden'
                                          },
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Common:StaticContentField:Separator',
                                                  name              : 'Common:StaticContentField:Separator',
                                                  referenceExisting : true,
                                                  referenceTypeName : 'isContentFor',
                                                  referenceDirection: 'src'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      },

                      // template
                      {
                          id      : 'templates',
                          key     : 'templates',
                          label   : __( 'YN.base.frontend.formDesigner.treeTable.templates' ),
                          icon    : resolveLigatureIcon( 'yn-tree-icon material-symbols-outlined ::table_chart::' ),
                          children: [],
                          data    : {
                              layoutElementDefinition: [

                                  // container with two columns
                                  {
                                      key  : 'TwoColumns',
                                      label: __( 'YN.base.frontend.formDesigner.templates.containerWithTwoColumns' ),
                                      icon : 'yn-icon  fas fa-table-columns',
                                      data : {
                                          itemTypeName  : 'Container',
                                          key           : 'Container',
                                          name          : 'Container',
                                          properties    : {
                                              IsLayoutWrapper: true
                                          },
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Container',
                                                  key               : 'Container',
                                                  name              : 'Container',
                                                  referenceTypeName : 'belongsTo',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Order    : 1,
                                                      Width    : 50,
                                                      WidthUnit: 'percent'
                                                  },
                                                  additionalData    : [
                                                      {
                                                          itemTypeName      : 'Message',
                                                          key               : 'Title',
                                                          name              : 'Title',
                                                          referenceTypeName : 'isTitleFor',
                                                          referenceDirection: 'src',
                                                          properties        : {
                                                              Type: 'message_catalog'
                                                          },
                                                          enumProperties    : {
                                                              Content: [
                                                                  {
                                                                      key  : 'DE',
                                                                      value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'DE' )
                                                                  },
                                                                  {
                                                                      key  : 'EN',
                                                                      value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'EN' )
                                                                  }
                                                              ]
                                                          }
                                                      }
                                                  ]
                                              },
                                              {
                                                  itemTypeName      : 'Container',
                                                  key               : 'Container',
                                                  name              : 'Container',
                                                  referenceTypeName : 'belongsTo',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Order    : 2,
                                                      Width    : 50,
                                                      WidthUnit: 'percent'
                                                  },
                                                  additionalData    : [
                                                      {
                                                          itemTypeName      : 'Message',
                                                          key               : 'Title',
                                                          name              : 'Title',
                                                          referenceTypeName : 'isTitleFor',
                                                          referenceDirection: 'src',
                                                          properties        : {
                                                              Type: 'message_catalog'
                                                          },
                                                          enumProperties    : {
                                                              Content: [
                                                                  {
                                                                      key  : 'DE',
                                                                      value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'DE' )
                                                                  },
                                                                  {
                                                                      key  : 'EN',
                                                                      value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'EN' )
                                                                  }
                                                              ]
                                                          }
                                                      }
                                                  ]
                                              }
                                          ]
                                      }
                                  },

                                  // TabContainer
                                  {
                                      key  : 'TabContainer',
                                      label: __( 'YN.base.frontend.formDesigner.templates.tabContainerWithTwoTabs' ),
                                      icon : 'yn-icon  fas fa-table-columns',
                                      data : {
                                          itemTypeName  : 'TabContainer',
                                          key           : 'TabContainer',
                                          name          : 'TabContainer',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Title',
                                                  name              : 'Title',
                                                  referenceTypeName : 'isTitleFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              },
                                              {
                                                  itemTypeName      : 'Container',
                                                  key               : 'Container',
                                                  name              : 'Container',
                                                  referenceTypeName : 'belongsTo',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Order: 1
                                                  },
                                                  additionalData    : [
                                                      {
                                                          itemTypeName      : 'Message',
                                                          key               : 'Title',
                                                          name              : 'Title',
                                                          referenceTypeName : 'isTitleFor',
                                                          referenceDirection: 'src',
                                                          properties        : {
                                                              Type: 'message_catalog'
                                                          },
                                                          enumProperties    : {
                                                              Content: [
                                                                  {
                                                                      key  : 'DE',
                                                                      value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'DE' )
                                                                  },
                                                                  {
                                                                      key  : 'EN',
                                                                      value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'EN' )
                                                                  }
                                                              ]
                                                          }
                                                      }
                                                  ]
                                              },
                                              {
                                                  itemTypeName      : 'Container',
                                                  key               : 'Container',
                                                  name              : 'Container',
                                                  referenceTypeName : 'belongsTo',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Order: 2
                                                  },
                                                  additionalData    : [
                                                      {
                                                          itemTypeName      : 'Message',
                                                          key               : 'Title',
                                                          name              : 'Title',
                                                          referenceTypeName : 'isTitleFor',
                                                          referenceDirection: 'src',
                                                          properties        : {
                                                              Type: 'message_catalog'
                                                          },
                                                          enumProperties    : {
                                                              Content: [
                                                                  {
                                                                      key  : 'DE',
                                                                      value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'DE' )
                                                                  },
                                                                  {
                                                                      key  : 'EN',
                                                                      value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.static.container.title', 'EN' )
                                                                  }
                                                              ]
                                                          }
                                                      }
                                                  ]
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      },

                      // fields
                      {
                          id      : 'fields',
                          key     : 'fields',
                          label   : __( 'YN.base.frontend.formDesigner.treeTable.fields' ),
                          icon    : 'yn-tree-icon pi pi-server',
                          children: [],
                          data    : {
                              layoutElementDefinition: [

                                  // BooleanField
                                  {
                                      key  : 'BooleanField',
                                      label: metaModel[ 'CFG' ].getItemType( 'BooleanField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'BooleanField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'BooleanField',
                                          key           : 'BooleanField',
                                          name          : 'BooleanField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // ColorField
                                  {
                                      key  : 'ColorField',
                                      label: metaModel[ 'CFG' ].getItemType( 'ColorField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'ColorField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'ColorField',
                                          key           : 'ColorField',
                                          name          : 'ColorField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // CoordinateField
                                  {
                                      key  : 'CoordinateField',
                                      label: metaModel[ 'CFG' ].getItemType( 'CoordinateField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'CoordinateField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'CoordinateField',
                                          key           : 'CoordinateField',
                                          name          : 'CoordinateField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // CurrencyField
                                  {
                                      key  : 'CurrencyField',
                                      label: metaModel[ 'CFG' ].getItemType( 'CurrencyField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'CurrencyField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'CurrencyField',
                                          key           : 'CurrencyField',
                                          name          : 'CurrencyField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // DateField
                                  {
                                      key  : 'DateField',
                                      label: metaModel[ 'CFG' ].getItemType( 'DateField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'DateField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'DateField',
                                          key           : 'DateField',
                                          name          : 'DateField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // DoubleField
                                  {
                                      key  : 'DoubleField',
                                      label: metaModel[ 'CFG' ].getItemType( 'DoubleField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'DoubleField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'DoubleField',
                                          key           : 'DoubleField',
                                          name          : 'DoubleField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // DurationField
                                  {
                                      key  : 'DurationField',
                                      label: metaModel[ 'CFG' ].getItemType( 'DurationField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'DurationField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'DurationField',
                                          key           : 'DurationField',
                                          name          : 'DurationField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // EmailField
                                  {
                                      key  : 'EmailField',
                                      label: metaModel[ 'CFG' ].getItemType( 'EmailField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'EmailField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'EmailField',
                                          key           : 'EmailField',
                                          name          : 'EmailField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // IbanField
                                  {
                                      key  : 'IbanField',
                                      label: metaModel[ 'CFG' ].getItemType( 'IbanField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IbanField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'IbanField',
                                          key           : 'IbanField',
                                          name          : 'IbanField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // ImageDataField
                                  {
                                      key  : 'ImageDataField',
                                      label: metaModel[ 'CFG' ].getItemType( 'ImageDataField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'ImageDataField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'ImageDataField',
                                          key           : 'ImageDataField',
                                          name          : 'ImageDataField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // IntegerField
                                  {
                                      key  : 'IntegerField',
                                      label: metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'IntegerField',
                                          key           : 'IntegerField',
                                          name          : 'IntegerField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // JsonField
                                  {
                                      key  : 'JsonField',
                                      label: metaModel[ 'CFG' ].getItemType( 'JsonField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'JsonField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'JsonField',
                                          key           : 'JsonField',
                                          name          : 'JsonField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // LinkField
                                  {
                                      key  : 'LinkField',
                                      label: metaModel[ 'CFG' ].getItemType( 'LinkField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'LinkField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'LinkField',
                                          key           : 'LinkField',
                                          name          : 'LinkField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // PhoneField
                                  {
                                      key  : 'PhoneField',
                                      label: metaModel[ 'CFG' ].getItemType( 'PhoneField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'PhoneField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'PhoneField',
                                          key           : 'PhoneField',
                                          name          : 'PhoneField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // StringField
                                  {
                                      key  : 'StringField',
                                      label: metaModel[ 'CFG' ].getItemType( 'StringField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'StringField',
                                          key           : 'StringField',
                                          name          : 'StringField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // TextField
                                  {
                                      key  : 'TextField',
                                      label: metaModel[ 'CFG' ].getItemType( 'TextField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TextField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'TextField',
                                          key           : 'TextField',
                                          name          : 'TextField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // TimeField
                                  {
                                      key  : 'TimeField',
                                      label: metaModel[ 'CFG' ].getItemType( 'TimeField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimeField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'TimeField',
                                          key           : 'TimeField',
                                          name          : 'TimeField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // TimestampField
                                  {
                                      key  : 'TimestampField',
                                      label: metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'TimestampField',
                                          key           : 'TimestampField',
                                          name          : 'TimestampField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  },
                                  // ValueSetField
                                  {
                                      key  : 'ValueSetField',
                                      label: metaModel[ 'CFG' ].getItemType( 'ValueSetField' )?.label,
                                      icon : resolveLigatureIcon( `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'ValueSetField' )?.icon}` ),
                                      data : {
                                          itemTypeName  : 'ValueSetField',
                                          key           : 'ValueSetField',
                                          name          : 'ValueSetField',
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Message',
                                                  key               : 'Label',
                                                  name              : 'Label',
                                                  referenceTypeName : 'isLabelFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'message_catalog'
                                                  },
                                                  enumProperties    : {
                                                      Content: [
                                                          {
                                                              key  : 'DE',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'DE' )
                                                          },
                                                          {
                                                              key  : 'EN',
                                                              value: messageCatalog.getContent( 'YN.base.frontend.formDesigner.fields.field.label', 'EN' )
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  }
                              ].sort( ( lED1, lED2 ) => {
                                  if ( ( lED1.label ?? '' ) > ( lED2.label ?? '' ) ) {
                                      return 1;
                                  }
                                  else if ( ( lED2.label ?? '' ) > ( lED1.label ?? '' ) ) {
                                      return -1;
                                  }
                                  else {
                                      return 0;
                                  }
                              } )
                          }
                      }
                  ];
              },
              [
                  language,
                  messageCatalogTimestamp
              ]
          ),
          selectedDataDescriptorAlias                = useMemo(
              () => {
                  let retVal : string | undefined = undefined;

                  if ( selectedLayoutElementId ) {
                      const selectedLayoutElementItem = global.data[ dataContext ]?.items[ selectedLayoutElementId ];

                      if ( selectedLayoutElementItem ) {
                          for ( const itemReferenceId of selectedLayoutElementItem.references ) {
                              const reference                                    = global.data[ dataContext ]?.references?.[ itemReferenceId ],
                                    dataObjectProvidesDataForItemReferenceTypeId = metaModel[ selectedLayoutElementItem.schema ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', selectedLayoutElementItem.itemTypeId, 'providesDataFor', 'src' )?.[ 0 ]?.id;

                              if (
                                  reference
                                  && reference.destItemId === selectedLayoutElementItem.id
                                  && reference.referenceSetId === dataObjectProvidesDataForItemReferenceTypeId
                                  && reference.srcItem
                              ) {
                                  const selectedLayoutElementItemAttributes = dataExtraction.extractPropertiesOfItem( selectedLayoutElementItem ),
                                        dataObjectAttributes                = dataExtraction.extractPropertiesOfItem( reference.srcItem ),
                                        attributePath                       = selectedLayoutElementItemAttributes.attributePath as string | undefined,
                                        dataDescriptor                      = attributePath && dataObjectAttributes ? buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties ) : undefined;

                                  if ( dataDescriptor?.alias ) {
                                      retVal = dataDescriptor.alias;
                                  }

                                  break;
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  globalDataVersions,
                  selectedLayoutElementId
              ]
          ),
          rootItem                                   = useMemo(
              () => {
                  return global.data[ dataContext ]?.items[ rootItemId ];
              },
              [
                  dataContext,
                  rootItemId,
                  globalDataVersions
              ]
          ),
          dataStructure                              = useMemo(
              () => {
                  let retVal = rootItem ? elusiveData?.dataStructure : undefined;

                  if (
                      (
                          !retVal
                          || dataContext !== lastParams.current?.dataContext
                          || globalDataVersions !== lastParams.current?.globalDataVersions
                          || rootItem !== lastParams.current?.formItem
                      )
                      && rootItem
                  ) {
                      const newDataStructure = dataExtraction.extractDataStructure( rootItem, dataContext, true );

                      if ( JSON.stringify( newDataStructure ?? {} ) !== JSON.stringify( retVal ?? {} ) ) {
                          retVal = newDataStructure;

                          dispatch( setElusiveData( uid, { dataStructure: newDataStructure } ) );
                      }
                  }

                  return retVal;
              },
              [
                  dataContext,
                  rootItem,
                  globalDataVersions
              ]
          ),
          addNewNode                                 = ( dataObjectToUse : DataObjectProperties, dataStructureToUse : DataStructure, item : ItemAttributesForCreation | Item, reference : ReferenceAttributesForCreation ) => {
              const dataObjectSchema       = ( dataObjectToUse.item.objectSchema ?? dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                    dataObjectItemType     = metaModel[ dataObjectSchema ].getItemType( item.itemTypeId )?.name,
                    dataObjectReferenceSet = metaModel[ dataObjectSchema ].getReferenceSet( reference.referenceSetId );

              if ( dataStructureToUse && dataObjectItemType && dataObjectReferenceSet ) {
                  const activeStatusId                      = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                        dataObjectItemTypeId                = metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                        objectTypePropertySetId             = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectType' )!.id,
                        objectSchemaPropertySetId           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectSchema' )!.id,
                        isReferencedToReferenceSetId        = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( dataObjectItemTypeId, dataObjectItemTypeId, 'isReferencedTo', 'dest' )![ 0 ].id,
                        referenceConfigurationPropertySetId = metaModel[ 'CFG' ].getReferencePropertySetForReferenceSetAndPropertyType( isReferencedToReferenceSetId, 'ReferenceConfiguration' )!.id,
                        referenceDirection                  = dataObjectReferenceSet.srcItemType.name === dataObjectReferenceSet.destItemType.name
                            ? reference.srcItemId
                                ? 'SrcToDest'
                                : 'DestToSrc'
                            : dataObjectReferenceSet.srcItemType.name === dataObjectItemType
                                ? 'SrcToDest'
                                : 'DestToSrc';

                  const newItem = dispatch( addItem(
                      dataContext,
                      {
                          statusId  : activeStatusId,
                          itemTypeId: dataObjectItemTypeId,
                          key       : `${rootItem.key}:${dataObjectItemType}:${uuidV4().substring( 0, 8 )}`,
                          name      : dataObjectItemType,
                          properties: [
                              {
                                  propertySetId: objectTypePropertySetId,
                                  value        : dataObjectItemType,
                                  statusId     : activeStatusId
                              },
                              {
                                  propertySetId: objectSchemaPropertySetId,
                                  value        : dataObjectSchema.toLowerCase(),
                                  statusId     : activeStatusId
                              }
                          ]
                      },
                      'CFG',
                      additionalElusiveDataContexts
                  ) );

                  const parentReference = dispatch( addReference(
                      dataContext,
                      {
                          referenceSetId: isReferencedToReferenceSetId,
                          statusId      : activeStatusId,
                          srcItemId     : newItem.id,
                          destItemId    : dataObjectToUse.origId,
                          enumProperties: [
                              {
                                  referenceId           : Reference.uniqueIdCount,
                                  referencePropertySetId: referenceConfigurationPropertySetId,
                                  properties            : [
                                      {
                                          referenceId           : Reference.uniqueIdCount,
                                          referencePropertySetId: referenceConfigurationPropertySetId,
                                          key                   : dataObjectReferenceSet?.referenceType.name,
                                          value                 : `{ "referenceDirection": "${referenceDirection}" }`,
                                          statusId              : activeStatusId
                                      }
                                  ]
                              }
                          ]
                      },
                      'CFG',
                      additionalElusiveDataContexts
                  ) );

                  if ( parentReference ) {
                      newItem.registerReference( parentReference );
                  }

                  // select new element
                  onSelectLayoutElement?.( newItem.id );
              }
          },
          deleteOrUndeleteNode                       = ( dataObjectToUse : DataObjectProperties ) => {
              const dataObjectItem              = global.data[ dataContext ].items[ dataObjectToUse.origId ],
                    dataObjectItemDeleted       = dataObjectItem.isDeleted(),
                    referenceToParentDataObject = global.data[ dataContext ]?.references?.[ dataObjectItem.references.find(
                        ( referenceId ) => {
                            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                            let retVal : boolean = false;

                            if (
                                reference
                                && reference.srcItemId === dataObjectItem.id
                                && reference.referenceSet.referenceType.name === 'isReferencedTo'
                            ) {
                                retVal = true
                            }

                            return retVal;
                        }
                    ) ?? 0 ],
                    formPanelKey                = nodeIdToPanelKeyMap?.[ dataObjectToUse.id ]?.replace( '_attributesForm', '' );

              // undelete
              if ( dataObjectItemDeleted ) {
                  if ( referenceToParentDataObject ) {
                      dispatch( updateReference(
                          dataContext,
                          referenceToParentDataObject.id,
                          { statusId: referenceToParentDataObject.attributesModified.get( 'statusId' ) as number },
                          additionalElusiveDataContexts
                      ) );
                  }

                  dispatch( updateItem(
                      dataContext,
                      dataObjectItem.id,
                      { statusId: dataObjectItem.attributesModified.get( 'statusId' ) as number },
                      additionalElusiveDataContexts
                  ) );
              }

              // delete
              else {
                  if ( referenceToParentDataObject ) {
                      dispatch( deleteReference(
                          dataContext,
                          referenceToParentDataObject.id,
                          true,
                          additionalElusiveDataContexts,
                          false
                      ) );
                  }

                  dispatch( deleteItem(
                      dataContext,
                      dataObjectItem.id,
                      true,
                      additionalElusiveDataContexts,
                      false,
                      false
                  ) );

                  // remove from selectionKeys if
                  if ( Object.keys( selectionKeys ?? {} ).includes( dataObjectToUse.id ) ) {
                      const newSelectionKeys = { ...selectionKeys };

                      delete newSelectionKeys[ dataObjectToUse.id ];
                      dispatch( setParam( uid, 'selectionKeys', newSelectionKeys ) );
                  }

                  // deselect if selected
                  if ( selectedLayoutElementId === dataObjectToUse.origId ) {
                      onSelectLayoutElement?.( undefined );
                  }
              }

              // clean up form
              if ( formPanelKey ) {
                  const formPanelKeyWithoutPrefix = formPanelKey.replace( parentUid + '_', '' ),
                        panelProps                = { ...getPanelProps( parentUid ) };

                  if ( panelProps ) {
                      // delete props of form
                      delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix ];
                      delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix + '_attributesForm' ];

                      // delete error of form
                      delete panelProps.panelProps.subPanelErrors[ formPanelKey ];
                      delete panelProps.panelProps.subPanelErrors[ formPanelKey + '_attributesForm' ];

                      setPanelProps( parentUid, panelProps );
                  }

                  // delete elusive data of form
                  dispatch( clearElusiveData( formPanelKey ) );
                  dispatch( clearElusiveData( formPanelKey + '_attributesForm' ) );
              }
          },
          buildAddNodeButton                         = ( nodeId : string, dataObjectToUse : DataStructure, dataStructureToUse : DataStructure ) => {
              const buttonId   = `${uid}_${nodeId}_addButton`,
                    getContent = () => {
                        const dataObjectSchema   = ( dataObjectToUse.item.objectSchema ?? dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                              dataObjectItemType = metaModel[ dataObjectSchema ].getItemType( dataObjectToUse.item.objectType ?? '' ),
                              parentDataObject   = getParentDataObject( dataObjectToUse.id, dataStructureToUse );

                        if ( dataObjectItemType ) {

                            // TODO: handle circular referenceSets

                            const allowedReferenceSets   = metaModel[ dataObjectSchema ].getReferenceSetsForItemType( dataObjectItemType.id ),
                                  definedReferenceSets   = ( [] as ReferenceSet[] ).concat(
                                          parentDataObject
                                              ? getReferenceSetsForDataObject( dataObjectToUse, dataStructureToUse, dataObjectSchema )
                                              : []
                                      )
                                      .concat(
                                          dataObjectToUse.dataStructure?.length
                                              ? dataObjectToUse.dataStructure
                                                  .reduce(
                                                      ( innerRetVal, childDataStructureToUse ) => {
                                                          innerRetVal = innerRetVal.concat( getReferenceSetsForDataObject( childDataStructureToUse, dataStructureToUse, dataObjectSchema ) );

                                                          return innerRetVal;
                                                      },
                                                      [] as ReferenceSet[]
                                                  )
                                              : []
                                      ),
                                  includeReferenceSetIds = allowedReferenceSets.reduce(
                                      ( innerRetVal, rSet ) => {
                                          if ( !definedReferenceSets.find( dRSet => dRSet.id === rSet.id ) ) {
                                              innerRetVal.push( rSet.id );
                                          }

                                          return innerRetVal;
                                      },
                                      [] as number[]
                                  ),
                                  allowedReferenceSetIds = allowedReferenceSets.reduce(
                                      ( innerRetVal, rSet ) => {
                                          if ( !definedReferenceSets.find( dRSet => dRSet.id === rSet.id ) ) {
                                              innerRetVal[ rSet.id ] = rSet.destItemType.id === dataObjectItemType.id ? 'dest' : 'src';
                                          }

                                          return innerRetVal;
                                      },
                                      {} as { [ referenceSetId : number ] : 'dest' | 'src' }
                                  );

                            return getAddItemDialog( {
                                uid                   : uid + '-AddItemReferenceDialog_button_content',
                                dataContext,
                                schema                : dataObjectSchema,
                                includeReferenceSets  : includeReferenceSetIds,
                                allowedReferenceSetIds: allowedReferenceSetIds,
                                targetItemId          : dataObjectToUse.origId,
                                addMode               : 'new',
                                rawMode               : false,
                                showAdditionalRawNames: false,
                                useThisLabel          : false,
                                onAddNode             : addNewNode.bind( null, dataObjectToUse, dataStructureToUse ),
                                onHide                : () => {
                                    nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                },
                                onInteraction         : () => {
                                    nodeButtons.current[ buttonId ].justInteracted = true;
                                    ignoreSelectionChange.current = true;
                                }
                            } );
                        }
                        else {
                            return <></>;
                        }
                    };

              nodeButtons.current[ buttonId ] = {
                  justInteracted: false
              };

              return <ContentButton
                  key={buttonId}
                  id={buttonId}

                  ref={el => {
                      if ( el ) {
                          nodeButtons.current[ buttonId ] = {
                              ...nodeButtons.current[ buttonId ],
                              content: el
                          };
                      }
                  }}

                  icon="pi pi-plus-circle yn-add"
                  rounded={true}
                  text={true}
                  horizontalContentAlign="left"
                  verticalContentAlign="bottom"

                  onExpand={() => {
                      const innerRetVal = !!getContent();

                      ignoreSelectionChange.current = true;

                      return innerRetVal;
                  }}
                  onCollapse={( _event, isOutsideClicked ) => {
                      const innerRetVal = !nodeButtons.current[ buttonId ].justInteracted && isOutsideClicked;

                      nodeButtons.current[ buttonId ].justInteracted = false;

                      ignoreSelectionChange.current = true;

                      return innerRetVal;
                  }}
                  content={getContent}
              />;
          },
          buildDeleteNodeButton                      = ( nodeId : string, dataObjectToUse : DataStructure ) => {
              const buttonId              = `${uid}_${nodeId}_deleteButton`,
                    dataObjectItem        = global.data[ dataContext ].items[ dataObjectToUse.origId ],
                    dataObjectItemDeleted = dataObjectItem.isDeleted();

              nodeButtons.current[ buttonId ] = {
                  justInteracted: false
              };

              return <ContentButton
                  key={buttonId}
                  id={buttonId}

                  ref={el => {
                      if ( el ) {
                          nodeButtons.current[ buttonId ] = {
                              ...nodeButtons.current[ buttonId ],
                              content: el
                          };
                      }
                  }}

                  icon={`pi ${dataObjectItemDeleted ? 'pi-plus-circle yn-dirty' : 'pi-minus-circle yn-delete'}`}
                  rounded={true}
                  text={true}
                  horizontalContentAlign="left"
                  verticalContentAlign="bottom"

                  onExpand={( event ) => {
                      let innerRetVal : boolean = true;

                      if ( event.altKey || dataObjectItemDeleted ) {
                          deleteOrUndeleteNode( dataObjectToUse );

                          innerRetVal = false;
                      }

                      ignoreSelectionChange.current = true;

                      return innerRetVal;
                  }}

                  content={!dataObjectItemDeleted
                      ? <Panel
                          key={`${buttonId}_panel`}
                          id={`${buttonId}_panel`}

                          style={{ width: '25rem' }}
                          className="yn-confirm-option-panel"
                      >
                          <label>
                              <span className="pi pi-question-circle yn-blue"></span>
                              {__( 'YN.base.designer.tree.item.delete.text', { itemKey: dataObjectItem.key, itemName: dataObjectItem.name, itemDisplayAttributeValue: dataObjectItem.displayAttributeValue } )}
                          </label>
                          <Button
                              label={__( 'YN.base.common.no' )}
                              className="p-button-sm p-button-secondary mr-3"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={() => {
                                  ignoreSelectionChange.current = true;

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                          <Button
                              label={__( 'YN.base.common.yes' )}
                              className="p-button-sm p-confirm-popup-accept"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={() => {
                                  deleteOrUndeleteNode( dataObjectToUse );

                                  ignoreSelectionChange.current = true;

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                      </Panel>
                      : null}
              />;
          },
          // TODO: external config
          buildNodeData                              = ( dataObjectToUse : DataStructure, dataStructureToUse : DataStructure, schema? : Schema, repeatedDataObjectsToUse? : number[] ) : ExtendTreeNode | undefined => {
              const parentDataObject             = getParentDataObject( dataObjectToUse.id, dataStructureToUse ),
                    dataObjectItem               = global.data[ dataContext ].items[ dataObjectToUse.origId ],
                    dataObjectItemDeleted        = dataObjectItem.isDeleted(),
                    dataObjectKey                = dataObjectToUse.id,
                    dataObjectSchema             = ( schema ?? dataObjectToUse.item.objectSchema ?? dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                    dataObjectItemType           = metaModel[ dataObjectSchema ].getItemType( dataObjectToUse.item.objectType ?? '' ),
                    dataObjectReferenceSet       = parentDataObject
                        ? getReferenceSetsForDataObject( dataObjectToUse, dataStructureToUse, dataObjectSchema )?.[ 0 ]
                        : undefined,
                    dataObjectReferenceTypeName  = dataObjectToUse.reference?.referenceConfiguration
                        ? Object.keys( dataObjectToUse.reference.referenceConfiguration )[ 0 ] as string
                        : undefined,
                    dataObjectReferenceDirection = dataObjectToUse.reference?.referenceConfiguration
                        ? Object.values( dataObjectToUse.reference.referenceConfiguration )[ 0 ].referenceDirection as string
                        : undefined;

              let retVal : ExtendTreeNode | undefined = undefined;

              if ( dataObjectItemType ) {
                  const payloadHasTo1Reference = dataObjectReferenceSet?.cardinality?.split( ':' )[ dataObjectItemType.id === dataObjectReferenceSet.srcItemTypeId ? 0 : 1 ] === '1',
                        allowedReferenceSets   = metaModel[ dataObjectSchema ].getReferenceSetsForItemType( dataObjectItemType.id ),
                        definedReferenceSets   = ( [] as ReferenceSet[] ).concat(
                                parentDataObject
                                    ? getReferenceSetsForDataObject( dataObjectToUse, dataStructureToUse, dataObjectSchema )
                                    : []
                            )
                            .concat(
                                dataObjectToUse.dataStructure?.length
                                    ? dataObjectToUse.dataStructure
                                        .reduce(
                                            ( innerRetVal, childDataStructureToUse ) => {
                                                innerRetVal = innerRetVal.concat( getReferenceSetsForDataObject( childDataStructureToUse, dataStructureToUse, dataObjectSchema ) );

                                                return innerRetVal;
                                            },
                                            [] as ReferenceSet[]
                                        )
                                    : []
                            );

                  retVal = {
                      id        : dataObjectKey,
                      key       : dataObjectKey,
                      dataObject: dataObjectToUse,
                      label     : dataObjectItemType.label,
                      selectable: !dataObjectItemDeleted,
                      className : 'yn-dataobject-node',
                      icon      : resolveLigatureIcon( `yn-tree-icon icon-itemtype-detail ${dataObjectItemType.icon}` ),
                      data      : {
                          isDeleted              : dataObjectItemDeleted,
                          buttons                : [],
                          dataDescriptorAlias    : [],
                          layoutElementDefinition: []
                      }
                  };

                  let attributePath : string,
                      dataDescriptor : DataDescriptor | undefined;

                  // special label for referenced Message or Image item
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  if ( ( dataObjectItemType.name === 'Message' || dataObjectItemType.name === 'Image' ) && ( dataObjectReferenceSet || dataObjectReferenceTypeName ) ) {
                      retVal.label = `${_.upperFirst( dataExtraction.extractIsForReferenceTypeName( dataObjectReferenceSet?.referenceTypeLabel ?? dataObjectReferenceTypeName ?? '' ) )} (${dataObjectItemType.label})`;
                  }

                  // dataDescriptorAlias
                  attributePath = 'item.id';
                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );
                  if ( dataDescriptor?.alias ) {
                      retVal.data.dataDescriptorAlias.push( dataDescriptor.alias.substring( 0, dataDescriptor.alias.indexOf( aliasSeparator ) ) );
                  }
                  attributePath = 'reference.id';
                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );
                  if ( dataDescriptor?.alias ) {
                      retVal.data.dataDescriptorAlias.push( dataDescriptor.alias.substring( 0, dataDescriptor.alias.indexOf( aliasSeparator ) ) );
                  }

                  // add button
                  if ( !readOnly && allowedReferenceSets.length - definedReferenceSets.length > 0 && !dataObjectItemDeleted ) {
                      retVal.data.buttons.push( buildAddNodeButton( dataObjectKey, dataObjectToUse, dataStructureToUse ) );
                  }
                  // delete/restore button
                  if ( !readOnly && parentDataObject ) {
                      retVal.data.buttons.push( buildDeleteNodeButton( dataObjectKey, dataObjectToUse ) );
                  }

                  nodeKeyToDataDescriptorAliasesRepresenting.current[ dataObjectKey ] = [];

                  // reference data ( don't show for root )
                  if ( parentDataObject && dataObjectReferenceSet ) {

                      // tooltip
                      if ( dataObjectReferenceDirection && dataObjectReferenceDirection !== '*' ) {
                          if ( dataObjectReferenceDirection === 'SrcToDest' ) {
                              retVal.tooltip = `${__( 'YN.base.common.reference.directionMarkerReversed' )} ${dataObjectReferenceSet.referenceTypeLabel} ${__( 'YN.base.common.reference.directionMarkerReversed' )} ${dataObjectReferenceSet.srcItemType.label}`;
                          }
                          else if ( dataObjectReferenceDirection === 'DestToSrc' ) {
                              retVal.tooltip = `${__( 'YN.base.common.reference.directionMarker' )} ${dataObjectReferenceSet.referenceTypeLabel} ${__( 'YN.base.common.reference.directionMarker' )} ${dataObjectReferenceSet.destItemType.label}`;
                          }
                      }
                      else {
                          retVal.tooltip = dataObjectReferenceSet.label;
                      }

                      // ReferenceField template
                      let key  = `${dataObjectReferenceSet.srcItemType.name}:${dataObjectReferenceSet.referenceType.name}:${dataObjectReferenceSet.destItemType.name}:id`,
                          name = `${dataObjectReferenceSet.label} id`;

                      // special handling for File as non-root
                      if ( dataObjectToUse.item.objectType === 'File' ) {
                          attributePath = 'reference.id';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                          if ( dataDescriptor?.alias ) {
                              nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                          }

                          retVal.data.layoutElementDefinition.push( {
                              type               : 'fileField',
                              key                : 'FileField',
                              label              : __( 'YN.base.frontend.formDesigner.fileField' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'FileField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptor?.alias,
                              data               : {
                                  itemTypeName: 'FileField',
                                  key,
                                  name,
                                  dataObjectId: dataObjectToUse.item.id,
                                  properties  : {
                                      Height      : 0,
                                      Presentation: 'grid'
                                  }
                              }
                          } );
                      }
                      else {
                          attributePath = 'reference.id';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                          if ( dataDescriptor?.alias ) {
                              nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                          }

                          retVal.data.layoutElementDefinition.push( {
                              type               : 'referenceField',
                              key                : 'ReferenceField',
                              label              : __( 'YN.base.frontend.formDesigner.referenceField' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'ReferenceField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptor?.alias,
                              data               : {
                                  itemTypeName: 'ReferenceField',
                                  key,
                                  name,
                                  dataObjectId: dataObjectToUse.item.id
                              }
                          } );
                      }

                      // reference attributes
                      attributePath = 'reference.id';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'reference',
                          key                : 'id',
                          label              : __( 'YN.base.common.attribute.id' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectReferenceSet.shortName}:id`,
                              name          : `${dataObjectReferenceSet.shortName} id`,
                              dataObjectId  : dataObjectToUse.item.id,
                              properties    : {
                                  AttributePath: attributePath,
                                  Alignment    : 'right'
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Format',
                                      key               : 'Number',
                                      name              : 'Number',
                                      referenceTypeName : 'isFormatFor',
                                      referenceDirection: 'src',
                                      properties        : {
                                          Type: 'Number'
                                      }
                                  },
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );


                      // reference meta
                      /* TODO: implement if needed
                          attributePath = 'reference.referenceTypeName';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                          if ( dataDescriptor?.alias ) {
                              nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                          }

                          retVal.data.layoutElementDefinition.push( {
                              type               : 'referenceMeta',
                              key                : 'referenceTypeName',
                              label              : __( 'YN.base.common.attribute.referenceTypeName' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptor?.alias,
                              data               : {
                                  itemTypeName  : 'StaticContentField',
                                  key           : `${dataObjectReferenceSet.shortName}:referenceTypeName`,
                                  name          : `${dataObjectReferenceSet.shortName} referenceTypeName`,
                                  dataObjectId  : dataObjectToUse.item.id,
                                  properties    : {
                                      AttributePath: attributePath
                                  },
                                  additionalData: [
                                      {
                                          itemTypeName      : 'Style',
                                          key               : 'FieldLike',
                                          name              : 'FieldLike',
                                          referenceTypeName : 'isStyleFor',
                                          referenceDirection: 'src',
                                          enumProperties    : {
                                              ClassName: [
                                                  {
                                                      key: 'yn-fieldlike'
                                                  }
                                              ]
                                          }
                                      }
                                  ]
                              }
                          } );*/

                      attributePath = 'reference.referenceSetId';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'referenceMeta',
                          key                : 'referenceSetId',
                          label              : __( 'YN.base.common.attribute.referenceSetId' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectReferenceSet.shortName}:referenceSetId`,
                              name          : `${dataObjectReferenceSet.shortName} referenceSetId`,
                              dataObjectId  : dataObjectToUse.item.id,
                              properties    : {
                                  AttributePath: attributePath,
                                  Alignment    : 'right'
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Format',
                                      key               : 'Number',
                                      name              : 'Number',
                                      referenceTypeName : 'isFormatFor',
                                      referenceDirection: 'src',
                                      properties        : {
                                          Type: 'Number'
                                      }
                                  },
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'reference.statusId';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'referenceMeta',
                          key                : 'statusId',
                          label              : __( 'YN.base.common.attribute.statusId' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectReferenceSet.shortName}:statusId`,
                              name          : `${dataObjectReferenceSet.shortName} statusId`,
                              dataObjectId  : dataObjectToUse.item.id,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'reference.srcItemId';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'referenceMeta',
                          key                : 'srcItemId',
                          label              : __( 'YN.base.common.attribute.srcItemId' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectReferenceSet.shortName}:srcItemId`,
                              name          : `${dataObjectReferenceSet.shortName} srcItemId`,
                              dataObjectId  : dataObjectToUse.item.id,
                              properties    : {
                                  AttributePath: attributePath,
                                  Alignment    : 'right'
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Format',
                                      key               : 'Number',
                                      name              : 'Number',
                                      referenceTypeName : 'isFormatFor',
                                      referenceDirection: 'src',
                                      properties        : {
                                          Type: 'Number'
                                      }
                                  },
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'reference.destItemId';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'referenceMeta',
                          key                : 'destItemId',
                          label              : __( 'YN.base.common.attribute.destItemId' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectReferenceSet.shortName}:destItemId`,
                              name          : `${dataObjectReferenceSet.shortName} destItemId`,
                              dataObjectId  : dataObjectToUse.item.id,
                              properties    : {
                                  AttributePath: attributePath,
                                  Alignment    : 'right'
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Format',
                                      key               : 'Number',
                                      name              : 'Number',
                                      referenceTypeName : 'isFormatFor',
                                      referenceDirection: 'src',
                                      properties        : {
                                          Type: 'Number'
                                      }
                                  },
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'reference.modifierUserId';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'referenceMeta',
                          key                : 'modifierUserId',
                          label              : __( 'YN.base.common.attribute.modifierUserId' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectReferenceSet.shortName}:modifierUserId`,
                              name          : `${dataObjectReferenceSet.shortName} modifierUserId`,
                              dataObjectId  : dataObjectToUse.item.id,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'reference.modificationTime';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'referenceMeta',
                          key                : 'modificationTime',
                          label              : __( 'YN.base.common.attribute.modificationTime' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectReferenceSet.shortName}:modificationTime`,
                              name          : `${dataObjectReferenceSet.shortName} modificationTime`,
                              dataObjectId  : dataObjectToUse.item.id,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'reference.validatorUserId';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'referenceMeta',
                          key                : 'validatorUserId',
                          label              : __( 'YN.base.common.attribute.validatorUserId' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectReferenceSet.shortName}:validatorUserId`,
                              name          : `${dataObjectReferenceSet.shortName} validatorUserId`,
                              dataObjectId  : dataObjectToUse.item.id,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'reference.validationTime';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'referenceMeta',
                          key                : 'validationTime',
                          label              : __( 'YN.base.common.attribute.validationTime' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectReferenceSet.shortName}:validationTime`,
                              name          : `${dataObjectReferenceSet.shortName} validationTime`,
                              dataObjectId  : dataObjectToUse.item.id,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );


                      // reference properties - get possible fields for all properties
                      const referencePropertySets = metaModel[ dataObjectSchema ].getReferencePropertySetsForReferenceSet( dataObjectReferenceSet.id );

                      referencePropertySets.sort(
                          ( pset1, pset2 ) => pset1.label.toLowerCase() > pset2.label.toLowerCase() ? 1 : ( pset1.label.toLowerCase() < pset2.label.toLowerCase() ? -1 : 0 )
                      );

                      for ( const referencePropertySet of referencePropertySets ) {
                          key = `${dataObjectReferenceSet.shortName}:${referencePropertySet.name}`;
                          name = `${dataObjectReferenceSet.shortName} ${referencePropertySet.name}`;

                          attributePath = `referenceProperty.${referencePropertySet.name}`;
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                          if ( dataDescriptor?.alias ) {
                              nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                          }

                          if ( referencePropertySet.isEnumerated ) {
                              if ( referencePropertySet.isKeySettable && referencePropertySet.isValueSettable ) {
                                  key += ':key_value';
                                  name += ' key/value';
                              }
                              else if ( referencePropertySet.isKeySettable ) {
                                  key += ':key';
                                  name += ' key';
                              }
                              else {
                                  key += ':value';
                                  name += ' value';
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'referenceProperty',
                                  key                : referencePropertySet.name,
                                  label              : referencePropertySet.label,
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'EnumField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName: 'EnumField',
                                      key,
                                      name,
                                      dataObjectId: dataObjectToUse.item.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );
                          }
                          else if ( referencePropertySet.valueSetId ) {
                              key += ':value';
                              name += ' value';

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'property',
                                  key                : referencePropertySet.name,
                                  label              : referencePropertySet.label,
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'ValueSetField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName: 'ValueSetField',
                                      key,
                                      name,
                                      dataObjectId: dataObjectToUse.item.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );
                          }
                          else {
                              name += ' value';

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'referenceProperty',
                                  key                : referencePropertySet.name,
                                  label              : referencePropertySet.label,
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldTypeToItemTypeMapping[ referencePropertySet.valueType ] )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName: fieldTypeToItemTypeMapping[ referencePropertySet.valueType ],
                                      key,
                                      name,
                                      dataObjectId: dataObjectToUse.item.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );
                          }


                          // reference property meta (non enumerated only)
                          if ( !referencePropertySet.isEnumerated ) {
                              /* TODO: implement if needed
                                  attributePath = `referenceProperty.${referencePropertySet.name}.propertyTypeName`;
                                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                                  if ( dataDescriptor?.alias ) {
                                      nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                                  }

                                  retVal.data.layoutElementDefinition.push( {
                                      type               : 'referenceMeta',
                                      key                : 'propertyTypeName',
                                      label              : __( 'YN.base.common.attribute.propertyTypeName' ),
                                      icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                      dataDescriptorAlias: dataDescriptor?.alias,
                                      data               : {
                                          itemTypeName  : 'StaticContentField',
                                          key           : `${dataObjectItemType.name}:${referencePropertySet.name}:propertyTypeName`,
                                          name          : `${dataObjectItemType.name}:${referencePropertySet.name} propertyTypeName`,
                                          dataObjectId  : dataObjectToUse.item.id,
                                          properties    : {
                                              AttributePath: attributePath
                                          },
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Style',
                                                  key               : 'FieldLike',
                                                  name              : 'FieldLike',
                                                  referenceTypeName : 'isStyleFor',
                                                  referenceDirection: 'src',
                                                  enumProperties    : {
                                                      ClassName: [
                                                          {
                                                              key: 'yn-fieldlike'
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  } );*/

                              attributePath = `referenceProperty.${referencePropertySet.name}.referencePropertySetId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'referencePropertySetId',
                                  label              : __( 'YN.base.common.attribute.referencePropertySetId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${referencePropertySet.name}:referencePropertySetId`,
                                      name          : `${dataObjectItemType.name}:${referencePropertySet.name} referencePropertySetId`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      properties    : {
                                          AttributePath: attributePath,
                                          Alignment    : 'right'
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Format',
                                              key               : 'Number',
                                              name              : 'Number',
                                              referenceTypeName : 'isFormatFor',
                                              referenceDirection: 'src',
                                              properties        : {
                                                  Type: 'Number'
                                              }
                                          },
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );

                              attributePath = `referenceProperty.${referencePropertySet.name}.statusId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'statusId',
                                  label              : __( 'YN.base.common.attribute.statusId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${referencePropertySet.name}:statusId`,
                                      name          : `${dataObjectItemType.name}:${referencePropertySet.name} statusId`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      properties    : {
                                          AttributePath: attributePath
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );

                              attributePath = `referenceProperty.${referencePropertySet.name}.modifierUserId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'modifierUserId',
                                  label              : __( 'YN.base.common.attribute.modifierUserId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${referencePropertySet.name}:modifierUserId`,
                                      name          : `${dataObjectItemType.name}:${referencePropertySet.name} modifierUserId`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      properties    : {
                                          AttributePath: attributePath
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );

                              attributePath = `referenceProperty.${referencePropertySet.name}.modificationTime`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'modificationTime',
                                  label              : __( 'YN.base.common.attribute.modificationTime' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${referencePropertySet.name}:modificationTime`,
                                      name          : `${dataObjectItemType.name}:${referencePropertySet.name} modificationTime`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      properties    : {
                                          AttributePath: attributePath
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );

                              attributePath = `referenceProperty.${referencePropertySet.name}.validatorUserId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'validatorUserId',
                                  label              : __( 'YN.base.common.attribute.validatorUserId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${referencePropertySet.name}:validatorUserId`,
                                      name          : `${dataObjectItemType.name}:${referencePropertySet.name} validatorUserId`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      properties    : {
                                          AttributePath: attributePath
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );

                              attributePath = `referenceProperty.${referencePropertySet.name}.validationTime`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'validationTime',
                                  label              : __( 'YN.base.common.attribute.validationTime' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${referencePropertySet.name}:validationTime`,
                                      name          : `${dataObjectItemType.name}:${referencePropertySet.name} validationTime`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      properties    : {
                                          AttributePath: attributePath
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );
                          }
                      }
                  }

                  // show regular fields for root always, for others only if they have :1 cardinality
                  if ( !parentDataObject || ( ( payloadHasTo1Reference || repeatedDataObjectsToUse?.includes( dataObjectToUse.origId ) ) && dataObjectItemType.name !== 'File' ) ) {
                      // item attributes - id, key, name
                      attributePath = 'item.id';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'item',
                          key                : 'id',
                          label              : __( 'YN.base.common.attribute.id' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectItemType.name}:id`,
                              name          : `${dataObjectItemType.name} id`,
                              dataObjectId  : dataObjectToUse.item.id,
                              isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                              properties    : {
                                  AttributePath: attributePath,
                                  Alignment    : 'right'
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Format',
                                      key               : 'Number',
                                      name              : 'Number',
                                      referenceTypeName : 'isFormatFor',
                                      referenceDirection: 'src',
                                      properties        : {
                                          Type: 'Number'
                                      }
                                  },
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'item.key';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      let fieldItemType = 'StringField';
                      if ( dataDescriptor && ( dataUtils.getDataValueType( dataDescriptor, dataObjectSchema ) ?? 'STRING' ) as PropertyValueType === 'INTEGER' ) {
                          fieldItemType = 'IntegerField';
                      }

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'item',
                          key                : 'key',
                          label              : getItemKeyDisplayName( dataObjectItemType.id, dataObjectSchema ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldItemType )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName: fieldItemType,
                              key         : `${dataObjectItemType.name}:key`,
                              name        : `${dataObjectItemType.name} key`,
                              dataObjectId: dataObjectToUse.item.id,
                              isRepeated  : !!parentDataObject && !payloadHasTo1Reference,
                              properties  : {
                                  AttributePath: attributePath
                              }
                          }
                      } );

                      attributePath = 'item.name';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'item',
                          key                : 'name',
                          label              : getItemNameDisplayName( dataObjectItemType.id, dataObjectSchema ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName: 'StringField',
                              key         : `${dataObjectItemType.name}:name`,
                              name        : `${dataObjectItemType.name} name`,
                              dataObjectId: dataObjectToUse.item.id,
                              isRepeated  : !!parentDataObject && !payloadHasTo1Reference,
                              properties  : {
                                  AttributePath: attributePath
                              }
                          }
                      } );


                      // item meta
                      /* TODO: implement if needed
          attributePath = 'item.itemTypeName';
          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

          if ( dataDescriptor?.alias ) {
              nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
          }

          retVal.data.layoutElementDefinition.push( {
              type               : 'meta',
              key                : 'itemTypeName',
              label              : __( 'YN.base.common.attribute.itemTypeName' ),
              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
              dataDescriptorAlias: dataDescriptor?.alias,
              data               : {
                  itemTypeName  : 'StaticContentField',
                  key           : `${dataObjectItemType.name}:itemTypeName`,
                  name          : `${dataObjectItemType.name} itemTypeName`,
                  dataObjectId  : dataObjectToUse.item.id,
                  isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                  properties    : {
                      AttributePath: attributePath
                  },
                  additionalData: [
                      {
                          itemTypeName      : 'Style',
                          key               : 'FieldLike',
                          name              : 'FieldLike',
                          referenceTypeName : 'isStyleFor',
                          referenceDirection: 'src',
                          enumProperties    : {
                              ClassName: [
                                  {
                                      key: 'yn-fieldlike'
                                  }
                              ]
                          }
                      }
                  ]
              }
          } );*/

                      attributePath = 'item.itemTypeId';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'meta',
                          key                : 'itemTypeId',
                          label              : __( 'YN.base.common.attribute.itemTypeId' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectItemType.name}:itemTypeId`,
                              name          : `${dataObjectItemType.name} itemTypeId`,
                              dataObjectId  : dataObjectToUse.item.id,
                              isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                              properties    : {
                                  AttributePath: attributePath,
                                  Alignment    : 'right'
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Format',
                                      key               : 'Number',
                                      name              : 'Number',
                                      referenceTypeName : 'isFormatFor',
                                      referenceDirection: 'src',
                                      isRepeated        : false,
                                      properties        : {
                                          Type: 'Number'
                                      }
                                  },
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'item.statusId';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'meta',
                          key                : 'statusId',
                          label              : __( 'YN.base.common.attribute.statusId' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectItemType.name}:statusId`,
                              name          : `${dataObjectItemType.name} statusId`,
                              dataObjectId  : dataObjectToUse.item.id,
                              isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'item.creatorUserId';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'meta',
                          key                : 'creatorUserId',
                          label              : __( 'YN.base.common.attribute.creatorUserId' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectItemType.name}:creatorUserId`,
                              name          : `${dataObjectItemType.name} creatorUserId`,
                              dataObjectId  : dataObjectToUse.item.id,
                              isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'item.creationTime';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'meta',
                          key                : 'creationTime',
                          label              : __( 'YN.base.common.attribute.creationTime' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectItemType.name}:creationTime`,
                              name          : `${dataObjectItemType.name} creationTime`,
                              dataObjectId  : dataObjectToUse.item.id,
                              isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'item.modifierUserId';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'meta',
                          key                : 'modifierUserId',
                          label              : __( 'YN.base.common.attribute.modifierUserId' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectItemType.name}:modifierUserId`,
                              name          : `${dataObjectItemType.name} modifierUserId`,
                              dataObjectId  : dataObjectToUse.item.id,
                              isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'item.modificationTime';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'meta',
                          key                : 'modificationTime',
                          label              : __( 'YN.base.common.attribute.modificationTime' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectItemType.name}:modificationTime`,
                              name          : `${dataObjectItemType.name} modificationTime`,
                              dataObjectId  : dataObjectToUse.item.id,
                              isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'item.validatorUserId';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'meta',
                          key                : 'validatorUserId',
                          label              : __( 'YN.base.common.attribute.validatorUserId' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectItemType.name}:validatorUserId`,
                              name          : `${dataObjectItemType.name} validatorUserId`,
                              dataObjectId  : dataObjectToUse.item.id,
                              isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );

                      attributePath = 'item.validationTime';
                      dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                      if ( dataDescriptor?.alias ) {
                          nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                      }

                      retVal.data.layoutElementDefinition.push( {
                          type               : 'meta',
                          key                : 'validationTime',
                          label              : __( 'YN.base.common.attribute.validationTime' ),
                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                          dataDescriptorAlias: dataDescriptor?.alias,
                          data               : {
                              itemTypeName  : 'StaticContentField',
                              key           : `${dataObjectItemType.name}:validationTime`,
                              name          : `${dataObjectItemType.name} validationTime`,
                              dataObjectId  : dataObjectToUse.item.id,
                              isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                              properties    : {
                                  AttributePath: attributePath
                              },
                              additionalData: [
                                  {
                                      itemTypeName      : 'Style',
                                      key               : 'FieldLike',
                                      name              : 'FieldLike',
                                      referenceTypeName : 'isStyleFor',
                                      referenceDirection: 'src',
                                      enumProperties    : {
                                          ClassName: [
                                              {
                                                  key: 'yn-fieldlike'
                                              }
                                          ]
                                      }
                                  }
                              ]
                          }
                      } );


                      // item properties - get possible fields for all properties
                      const propertySets = metaModel[ dataObjectSchema ].getPropertySetsForItemType( dataObjectItemType.id );

                      propertySets.sort(
                          ( pset1, pset2 ) => pset1.label.toLowerCase() > pset2.label.toLowerCase() ? 1 : ( pset1.label.toLowerCase() < pset2.label.toLowerCase() ? -1 : 0 )
                      );

                      for ( const propertySet of propertySets ) {
                          let key  = `${dataObjectItemType.name}:${propertySet.name}`,
                              name = `${dataObjectItemType.name} ${propertySet.name}`;

                          attributePath = `property.${propertySet.name}`;
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                          if ( dataDescriptor?.alias ) {
                              nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                          }

                          if ( propertySet.isEnumerated ) {
                              if ( propertySet.isKeySettable && propertySet.isValueSettable ) {
                                  key += ':key_value';
                                  name += ' key/value';
                              }
                              else if ( propertySet.isKeySettable ) {
                                  key += ':key';
                                  name += ' key';
                              }
                              else {
                                  key += ':value';
                                  name += ' value';
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'property',
                                  key                : propertySet.name,
                                  label              : propertySet.label,
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'EnumField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName: 'EnumField',
                                      key,
                                      name,
                                      dataObjectId: dataObjectToUse.item.id,
                                      isRepeated  : !!parentDataObject && !payloadHasTo1Reference,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );
                          }
                          else if ( propertySet.valueSetId ) {
                              key += ':value';
                              name += ' value';

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'property',
                                  key                : propertySet.name,
                                  label              : propertySet.label,
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'ValueSetField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName: 'ValueSetField',
                                      key,
                                      name,
                                      dataObjectId: dataObjectToUse.item.id,
                                      isRepeated  : !!parentDataObject && !payloadHasTo1Reference,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );
                          }
                          else {
                              key += ':value';
                              name += ' value';

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'property',
                                  key                : propertySet.name,
                                  label              : propertySet.label,
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldTypeToItemTypeMapping[ propertySet.valueType ] )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName: fieldTypeToItemTypeMapping[ propertySet.valueType ],
                                      key,
                                      name,
                                      dataObjectId: dataObjectToUse.item.id,
                                      isRepeated  : !!parentDataObject && !payloadHasTo1Reference,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );
                          }


                          // property meta (non enumerated only)
                          if ( !propertySet.isEnumerated ) {
                              /* TODO: implement if needed
                                  attributePath = `property.${propertySet.name}.propertyTypeName`;
                                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                                  if ( dataDescriptor?.alias ) {
                                      nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                                  }

                                  retVal.data.layoutElementDefinition.push( {
                                      type               : 'meta',
                                      key                : 'propertyTypeName',
                                      label              : __( 'YN.base.common.attribute.propertyTypeName' ),
                                      icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                      dataDescriptorAlias: dataDescriptor?.alias,
                                      data               : {
                                          itemTypeName  : 'StaticContentField',
                                          key           : `${dataObjectItemType.name}:${propertySet.name}:propertyTypeName`,
                                          name          : `${dataObjectItemType.name}:${propertySet.name} propertyTypeName`,
                                          dataObjectId  : dataObjectToUse.item.id,
                                          isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                                          properties    : {
                                              AttributePath: attributePath
                                          },
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Style',
                                                  key               : 'FieldLike',
                                                  name              : 'FieldLike',
                                                  referenceTypeName : 'isStyleFor',
                                                  referenceDirection: 'src',
                                                  enumProperties    : {
                                                      ClassName: [
                                                          {
                                                              key: 'yn-fieldlike'
                                                          }
                                                      ]
                                                  }
                                              }
                                          ]
                                      }
                                  } );*/

                              attributePath = `property.${propertySet.name}.propertySetId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'meta',
                                  key                : 'propertySetId',
                                  label              : __( 'YN.base.common.attribute.propertySetId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${propertySet.name}:propertySetId`,
                                      name          : `${dataObjectItemType.name}:${propertySet.name} propertySetId`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                                      properties    : {
                                          AttributePath: attributePath,
                                          Alignment    : 'right'
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Format',
                                              key               : 'Number',
                                              name              : 'Number',
                                              referenceTypeName : 'isFormatFor',
                                              referenceDirection: 'src',
                                              isRepeated        : false,
                                              properties        : {
                                                  Type: 'Number'
                                              }
                                          },
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );

                              attributePath = `property.${propertySet.name}.statusId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'meta',
                                  key                : 'statusId',
                                  label              : __( 'YN.base.common.attribute.statusId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${propertySet.name}:statusId`,
                                      name          : `${dataObjectItemType.name}:${propertySet.name} statusId`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                                      properties    : {
                                          AttributePath: attributePath
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );

                              attributePath = `property.${propertySet.name}.modifierUserId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'meta',
                                  key                : 'modifierUserId',
                                  label              : __( 'YN.base.common.attribute.modifierUserId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${propertySet.name}:modifierUserId`,
                                      name          : `${dataObjectItemType.name}:${propertySet.name} modifierUserId`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                                      properties    : {
                                          AttributePath: attributePath
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );

                              attributePath = `property.${propertySet.name}.modificationTime`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'meta',
                                  key                : 'modificationTime',
                                  label              : __( 'YN.base.common.attribute.modificationTime' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${propertySet.name}:modificationTime`,
                                      name          : `${dataObjectItemType.name}:${propertySet.name} modificationTime`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                                      properties    : {
                                          AttributePath: attributePath
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );

                              attributePath = `property.${propertySet.name}.validatorUserId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'meta',
                                  key                : 'validatorUserId',
                                  label              : __( 'YN.base.common.attribute.validatorUserId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${propertySet.name}:validatorUserId`,
                                      name          : `${dataObjectItemType.name}:${propertySet.name} validatorUserId`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                                      properties    : {
                                          AttributePath: attributePath
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );

                              attributePath = `property.${propertySet.name}.validationTime`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectToUse.item } as unknown as DataObjectProperties );

                              if ( dataDescriptor?.alias ) {
                                  nodeKeyToDataDescriptorAliasesRepresenting.current[ retVal.key ].push( dataDescriptor.alias );
                              }

                              retVal.data.layoutElementDefinition.push( {
                                  type               : 'meta',
                                  key                : 'validationTime',
                                  label              : __( 'YN.base.common.attribute.validationTime' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptor?.alias,
                                  data               : {
                                      itemTypeName  : 'StaticContentField',
                                      key           : `${dataObjectItemType.name}:${propertySet.name}:validationTime`,
                                      name          : `${dataObjectItemType.name}:${propertySet.name} validationTime`,
                                      dataObjectId  : dataObjectToUse.item.id,
                                      isRepeated    : !!parentDataObject && !payloadHasTo1Reference,
                                      properties    : {
                                          AttributePath: attributePath
                                      },
                                      additionalData: [
                                          {
                                              itemTypeName      : 'Style',
                                              key               : 'FieldLike',
                                              name              : 'FieldLike',
                                              referenceTypeName : 'isStyleFor',
                                              referenceDirection: 'src',
                                              enumProperties    : {
                                                  ClassName: [
                                                      {
                                                          key: 'yn-fieldlike'
                                                      }
                                                  ]
                                              }
                                          }
                                      ]
                                  }
                              } );
                          }
                      }
                  }
              }

              return retVal;
          },
          buildTreeData                              = ( dataStructureToUse : DataStructure | undefined, repeatedDataObjectsToUse : number[] | undefined = repeatedDataObjects ) : ExtendTreeNode[] => {
              const retVal : ExtendTreeNode[]                   = ( [] as ExtendTreeNode[] ).concat( staticTreeNodes as ExtendTreeNode[] ),
                    newExpandedKeys : TreeTableExpandedKeysType = {},
                    newSelectionKeys                            = { ...selectionKeys };

              if ( dataStructureToUse ) {
                  const schema                 = ( dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                        buildNodeDataRecursive = ( dataStructureToProcess : DataStructure ) => {
                            const nodeData = buildNodeData( dataStructureToProcess, dataStructureToUse, schema, repeatedDataObjectsToUse );

                            if ( nodeData ) {
                                newExpandedKeys[ nodeData.key ] = true;

                                // process children only if not deleted
                                if ( !global.data[ dataContext ].items[ dataStructureToProcess.origId ].isDeleted() && dataStructureToProcess.dataStructure?.length ) {
                                    nodeData.children = [];

                                    for ( const childDataStructureToProcess of dataStructureToProcess.dataStructure ) {
                                        const childNodeData = buildNodeDataRecursive( childDataStructureToProcess );

                                        if ( childNodeData ) {
                                            newExpandedKeys[ childNodeData.key ] = true;
                                            nodeData.children.push( childNodeData );
                                        }
                                    }

                                    // sort by label
                                    nodeData.children.sort( ( childNodeData1, childNodeData2 ) => {
                                        if ( ( childNodeData1.label ?? '' ) > ( childNodeData2.label ?? '' ) ) {
                                            return 1;
                                        }
                                        else if ( ( childNodeData2.label ?? '' ) > ( childNodeData1.label ?? '' ) ) {
                                            return -1;
                                        }
                                        else {
                                            return 0;
                                        }
                                    } )
                                }
                            }

                            return nodeData;
                        };

                  const nodeData = buildNodeDataRecursive( dataStructureToUse );

                  if ( nodeData ) {
                      retVal.push( nodeData );

                      if ( !Object.keys( newSelectionKeys ).length ) {
                          newSelectionKeys[ nodeData.key ] = true;
                      }
                  }
              }

              dispatch( setParams( uid, {
                  expandedKeys : newExpandedKeys,
                  selectionKeys: newSelectionKeys
              } ) );

              return retVal;
          },
          treeData                                   = useMemo(
              () => {
                  let retVal = elusiveData?.treeData;

                  if (
                      !retVal
                      || dataStructure !== lastParams.current?.dataStructure
                      || dataContext !== lastParams.current?.dataContext
                      || globalDataVersions !== lastParams.current?.globalDataVersions
                      || rootItem !== lastParams.current?.rootItem
                      || language !== lastParams.current?.language
                      || messageCatalogTimestamp !== lastParams.current?.messageCatalogTimestamp
                      || metaModelTimestamp !== lastParams.current?.metaModelTimestamp
                      || repeatedDataObjects !== lastParams.current?.repeatedDataObjects
                      || staticTreeNodes !== lastParams.current?.staticTreeNodes
                  ) {
                      retVal = buildTreeData( dataStructure, repeatedDataObjects );

                      dispatch( setElusiveData( uid, { treeData: retVal } ) );
                  }

                  return retVal;
              },
              [
                  dataContext,
                  dataStructure,
                  globalDataVersions,
                  language,
                  messageCatalogTimestamp,
                  metaModelTimestamp,
                  rootItem,
                  repeatedDataObjects,
                  staticTreeNodes
              ]
          ),
          dataDescriptorAliasesInUse                 = useMemo(
              () => {
                  const retVal : { [ dataObjectId : string ] : string[] } = {};

                  if ( dataStructure ) {
                      const dataObjects = extractDataObjectsFromDataStructure( dataStructure );

                      for ( const dataObject of dataObjects ) {
                          const dataObjectItem = global.data[ dataContext ].items[ dataObject.origId ];

                          for ( const referenceId of dataObjectItem?.references ?? [] ) {
                              const reference = global.data[ dataContext ]?.references[ referenceId ];

                              if (
                                  reference
                                  && reference.srcItemId === dataObject.origId
                                  && reference.referenceSet.referenceType.name === 'providesDataFor'
                                  && reference.destItem
                              ) {
                                  let attributePath : string | undefined;

                                  if ( reference.destItem.itemType.name === 'FileField' || reference.destItem.itemType.name === 'ReferenceField' ) {
                                      attributePath = 'reference.id';
                                  }
                                  else {
                                      const destItemProperties = dataExtraction.extractPropertiesOfItem( reference.destItem );

                                      if ( destItemProperties.attributePath ) {
                                          attributePath = destItemProperties.attributePath;
                                      }
                                  }

                                  const dataDescriptor      = attributePath ? buildDataDescriptor( { attributePath }, { item: dataObjectItem } as unknown as DataObjectProperties ) : undefined,
                                        dataDescriptorAlias = dataDescriptor?.alias;

                                  if ( dataDescriptorAlias ) {
                                      if ( !retVal[ dataObject.id ] ) {
                                          retVal[ dataObject.id ] = [];
                                      }

                                      retVal[ dataObject.id ].push( dataDescriptorAlias );
                                  }
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  dataContext,
                  dataStructure,
                  globalDataVersions
              ]
          ),
          getUniqueIdentifierPart                    = useCallback(
              () => {
                  return uuidV4().substring( 0, 8 );
              },
              [
                  'onlyOnce'
              ]
          ),
          // TODO: external config
          onDoubleClick                              = useCallback(
              ( dataConfig : DataConfig ) => {
                  let parentLayoutContainer : Item | undefined;

                  // get selected element if Container
                  if ( selectedLayoutElementId ) {
                      parentLayoutContainer = global.data[ dataContext ].items[ selectedLayoutElementId ];

                      if ( parentLayoutContainer?.itemType.name !== 'Container' ) {
                          parentLayoutContainer = undefined;
                      }
                  }

                  // get last Container of Form (which is not a layoutWrapper)
                  if ( !parentLayoutContainer ) {
                      const extractContainerStructure = ( layoutElementId : number ) => {
                                const retVal : ContainerStructure[] = [],
                                      item                          = global.data[ dataContext ]?.items?.[ layoutElementId ];

                                for ( const referenceId of item?.references ?? [] ) {
                                    const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                                    if (
                                        reference
                                        && !reference.isDeleted()
                                        && reference.destItemId === item.id
                                        && global.data[ dataContext ]?.items?.[ reference.srcItemId ]
                                    ) {
                                        const referenceSrcItem = global.data[ dataContext ].items[ reference.srcItemId ];

                                        if ( ( referenceSrcItem.itemType.name === 'Container' || referenceSrcItem.itemType.name === 'TabContainer' ) && referenceSrcItem.id !== layoutElementId ) {
                                            const containerData : LoadContainerData | LoadTabContainerData = referenceSrcItem.itemType.name === 'Container'
                                                      ? {
                                                          type       : 'CONTAINER',
                                                          data       : {
                                                              ...dataExtraction.extractPropertiesOfItem( referenceSrcItem )
                                                          },
                                                          permissions: {}
                                                      }
                                                      : {
                                                          type       : 'TABCONTAINER',
                                                          data       : {
                                                              ...dataExtraction.extractPropertiesOfItem( referenceSrcItem )
                                                          },
                                                          permissions: {}
                                                      },
                                                  containerSubContainerStructure                           = extractContainerStructure( containerData.data.id );

                                            retVal.push( {
                                                ...containerData,
                                                children: containerSubContainerStructure
                                            } );
                                        }
                                    }
                                }

                                return retVal;
                            },
                            containerStructure        = extractContainerStructure( rootItemId as number );

                      if ( containerStructure.length ) {
                          const orderContainerStructure = ( containerStructure1 : LoadContainerData | LoadTabContainerData, containerStructure2 : LoadContainerData | LoadTabContainerData ) : -1 | 0 | 1 => {
                                    const layoutStructure1Order = containerStructure1.data.order ?? 0,
                                          layoutStructure2Order = containerStructure2.data.order ?? 0;

                                    if ( layoutStructure1Order > layoutStructure2Order ) {
                                        return 1;
                                    }
                                    else if ( layoutStructure1Order < layoutStructure2Order ) {
                                        return -1;
                                    }
                                    else {
                                        return 0;
                                    }
                                },
                                getLastContainer        = ( containerStructureToSearchIn : ContainerStructure[] ) => {
                                    let retVal : Item | undefined;

                                    containerStructureToSearchIn.sort( orderContainerStructure );

                                    for ( let i = containerStructureToSearchIn.length - 1; i >= 0; i-- ) {
                                        if ( containerStructureToSearchIn[ i ].type === 'CONTAINER' && !( containerStructureToSearchIn[ i ] as LoadContainerData ).data.isLayoutWrapper ) {
                                            retVal = global.data[ dataContext ].items[ containerStructureToSearchIn[ i ].data.id ];
                                        }

                                        if ( !retVal && containerStructureToSearchIn[ i ].children.length ) {
                                            retVal = getLastContainer( containerStructureToSearchIn[ i ].children );
                                        }

                                        if ( retVal ) {
                                            break;
                                        }
                                    }

                                    return retVal;
                                };

                          parentLayoutContainer = getLastContainer( containerStructure );
                      }
                  }

                  // set order to be last in parentLayoutContainer (append)
                  if ( parentLayoutContainer ) {
                      let maxOrder : number = 0;

                      for ( const referenceId of parentLayoutContainer?.references ?? [] ) {
                          const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                          if (
                              reference
                              && !reference.isDeleted()
                              && reference.referenceSet.referenceType.name === 'belongsTo'
                              && reference.destItemId === parentLayoutContainer.id
                              && reference.srcItemId !== parentLayoutContainer.id
                              && global.data[ dataContext ]?.items?.[ reference.srcItemId ]
                          ) {
                              const referenceSrcItem = global.data[ dataContext ].items[ reference.srcItemId ];

                              if ( !referenceSrcItem.isDeleted() ) {
                                  const data = dataExtraction.extractPropertiesOfItem( referenceSrcItem );

                                  if ( ( data.order ?? 0 ) > maxOrder ) {
                                      maxOrder = data.order;
                                  }
                              }
                          }
                      }

                      dataConfig.properties = {
                          ...dataConfig.properties,
                          Order: maxOrder + 10
                      };
                  }

                  if ( parentLayoutContainer ) {
                      const
                          fieldItemTypeId = metaModel[ 'CFG' ].getItemType( dataConfig.itemTypeName )?.id,
                          fieldItem       = fieldItemTypeId
                              ? new Item(
                                  {
                                      itemTypeId: fieldItemTypeId,
                                      key       : `${rootItem.key}:${dataConfig.key}:${getUniqueIdentifierPart()}`,
                                      name      : dataConfig.name
                                  },
                                  dataContext,
                                  'CFG'
                              )
                              : undefined;

                      if ( fieldItem ) {

                          // add reference to parentLayoutContainer
                          const parentReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( fieldItem.itemType.name, parentLayoutContainer.itemType.name, 'belongsTo', 'src' )?.[ 0 ]?.id,
                                parentReference      = parentReferenceSetId
                                    ? new Reference(
                                        {
                                            referenceSetId: parentReferenceSetId,
                                            srcItemId     : fieldItem.id,
                                            destItemId    : parentLayoutContainer.id
                                        },
                                        dataContext,
                                        'CFG'
                                    )
                                    : undefined;

                          if ( parentReference ) {
                              fieldItem.registerReference( parentReference );
                          }

                          // add reference to DataObject
                          if ( dataConfig.dataObjectId ) {
                              const referenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', fieldItem.itemType.name, 'providesDataFor', 'src' )?.[ 0 ]?.id,
                                    reference      = referenceSetId && dataConfig.dataObjectId
                                        ? new Reference(
                                            {
                                                referenceSetId,
                                                srcItemId : dataConfig.dataObjectId,
                                                destItemId: fieldItem.id
                                            },
                                            dataContext,
                                            'CFG'
                                        )
                                        : undefined;

                              if ( reference ) {
                                  fieldItem.registerReference( reference );
                              }
                          }

                          // set width
                          if ( newLayoutElementsWidth ) {
                              const width        = Number( newLayoutElementsWidth.replace( /^(\d*)(.*)$/, '$1' ) ),
                                    widthUnitRaw = newLayoutElementsWidth.replace( /^(\d*)(.*)$/, '$2' );

                              let widthUnit : string = 'gritunit';

                              switch ( widthUnitRaw ) {
                                  case '%':
                                      widthUnit = 'percent';
                                      break;
                                  case 'px':
                                      widthUnit = 'pixel';
                                      break;
                                  case 'rem':
                                      widthUnit = 'rem';
                                      break;
                              }

                              dataConfig.properties = {
                                  ...dataConfig.properties,
                                  Width    : width,
                                  WidthUnit: widthUnit
                              };
                          }

                          // add properties
                          if ( dataConfig.properties ) {
                              for ( const propertyTypeName of Object.keys( dataConfig.properties ) ) {
                                  const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                                  if ( propertyTypeId ) {
                                      fieldItem.addProperty( propertyTypeId ).value = dataConfig.properties[ propertyTypeName ];
                                  }
                              }
                          }

                          // add enumProperties
                          if ( dataConfig.enumProperties ) {
                              for ( const propertyTypeName of Object.keys( dataConfig.enumProperties ) ) {
                                  const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                                  if ( propertyTypeId ) {
                                      const enumProperty = fieldItem.addEnumProperty( propertyTypeId );

                                      for ( const propertyDef of dataConfig.enumProperties[ propertyTypeName ] ) {
                                          const property = enumProperty.addProperty( propertyDef.key ?? '' );

                                          if ( propertyDef.value ) {
                                              property.value = propertyDef.value;
                                          }
                                      }
                                  }
                              }
                          }

                          // add additionalData
                          if ( dataConfig.additionalData?.length ) {
                              const processAdditionalDataConfigRecursive = ( additionalDataConfig : Required<DataConfig>[ 'additionalData' ], innerParentItem : Item ) => {
                                  for ( const payload of additionalDataConfig ) {
                                      let item : Item | undefined;

                                      if ( !payload.referenceExisting ) {
                                          const itemTypeId = metaModel[ 'CFG' ].getItemType( payload.itemTypeName )?.id;

                                          item = itemTypeId
                                              ? new Item(
                                                  {
                                                      itemTypeId,
                                                      key : `${rootItem.key}:${payload.key}:${getUniqueIdentifierPart()}`,
                                                      name: payload.name
                                                  },
                                                  dataContext,
                                                  'CFG'
                                              )
                                              : undefined;
                                      }
                                      else {
                                          item = payload.item
                                              ? new Item(
                                                  payload.item,
                                                  dataContext,
                                                  'CFG'
                                              )
                                              : undefined;
                                      }

                                      if ( item ) {
                                          const referenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( payload.itemTypeName, innerParentItem.itemTypeId, payload.referenceTypeName, payload.referenceDirection )?.[ 0 ]?.id,
                                                reference      = item && referenceSetId
                                                    ? new Reference(
                                                        {
                                                            referenceSetId,
                                                            srcItemId : payload.referenceDirection === 'src' ? item.id : innerParentItem.id,
                                                            destItemId: payload.referenceDirection === 'dest' ? item.id : innerParentItem.id
                                                        },
                                                        dataContext,
                                                        'CFG'
                                                    )
                                                    : undefined;

                                          if ( reference ) {
                                              innerParentItem.registerReference( reference );
                                              item.registerReference( reference );

                                              if ( !payload.referenceExisting ) {
                                                  if ( payload.properties ) {
                                                      for ( const propertyTypeName of Object.keys( payload.properties ) ) {
                                                          const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                                                          if ( propertyTypeId ) {
                                                              item.addProperty( propertyTypeId ).value = payload.properties[ propertyTypeName ];
                                                          }
                                                      }
                                                  }

                                                  if ( payload.enumProperties ) {
                                                      for ( const propertyTypeName of Object.keys( payload.enumProperties ) ) {
                                                          const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                                                          if ( propertyTypeId ) {
                                                              const enumProperty = item.addEnumProperty( propertyTypeId );

                                                              for ( const propertyDef of payload.enumProperties[ propertyTypeName ] ) {
                                                                  const property = enumProperty.addProperty( propertyDef.key ?? '' );

                                                                  if ( propertyDef.value ) {
                                                                      property.value = propertyDef.value;
                                                                  }
                                                              }
                                                          }
                                                      }
                                                  }
                                              }

                                              if ( payload.additionalData?.length ) {
                                                  processAdditionalDataConfigRecursive( payload.additionalData, item );
                                              }
                                          }
                                      }
                                  }
                              };

                              processAdditionalDataConfigRecursive( dataConfig.additionalData, fieldItem );
                          }

                          // propagate
                          dispatch( addItem(
                              dataContext,
                              fieldItem,
                              'CFG',
                              [ uid ]
                          ) );

                          // select new element
                          onSelectLayoutElement?.( fieldItem.id );
                      }
                  }
              },
              [
                  dataContext,
                  newLayoutElementsWidth,
                  onSelectLayoutElement,
                  rootItem,
                  selectedLayoutElementId,
                  uid
              ]
          ),
          // TODO: external config
          onDragStart                                = useCallback(
              ( dataConfig : DataConfig, options : DragOptions, event : React.DragEvent<HTMLParagraphElement> ) => {
                  const fieldItemTypeId = metaModel[ 'CFG' ].getItemType( dataConfig.itemTypeName )?.id,
                        fieldItem       = fieldItemTypeId
                            ? new Item(
                                {
                                    itemTypeId: fieldItemTypeId,
                                    key       : `${rootItem.key}:${dataConfig.key}:${getUniqueIdentifierPart()}`,
                                    name      : dataConfig.name
                                }, undefined,
                                'CFG',
                                true
                            )
                            : undefined;

                  if ( fieldItem ) {
                      const data : DragData = {
                          items     : {},
                          references: {}
                      };

                      if ( dataConfig.dataObjectId ) {
                          const referenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', dataConfig.itemTypeName, 'providesDataFor', 'src' )?.[ 0 ]?.id,
                                reference      = referenceSetId && dataConfig.dataObjectId
                                    ? new Reference( { referenceSetId, srcItemId: dataConfig.dataObjectId, destItemId: fieldItem.id }, undefined, 'CFG', true )
                                    : undefined;

                          if ( reference ) {
                              fieldItem.registerReference( reference );

                              data.items[ fieldItem.id ] = fieldItem;
                              data.references![ reference.id ] = reference;
                              data.isRepeatedForDataObjectId = dataConfig.isRepeated ? dataConfig.dataObjectId : undefined;
                          }
                      }
                      else {
                          data.items[ fieldItem.id ] = fieldItem;
                      }

                      if ( newLayoutElementsWidth ) {
                          const width        = Number( newLayoutElementsWidth.replace( /^(\d*)(.*)$/, '$1' ) ),
                                widthUnitRaw = newLayoutElementsWidth.replace( /^(\d*)(.*)$/, '$2' );

                          let widthUnit : string = 'gritunit';

                          switch ( widthUnitRaw ) {
                              case '%':
                                  widthUnit = 'percent';
                                  break;
                              case 'px':
                                  widthUnit = 'pixel';
                                  break;
                              case 'rem':
                                  widthUnit = 'rem';
                                  break;
                          }

                          dataConfig.properties = {
                              ...dataConfig.properties,
                              Width    : width,
                              WidthUnit: widthUnit
                          };
                      }

                      if ( Object.keys( data.items ).length ) {
                          if ( dataConfig.properties ) {
                              for ( const propertyTypeName of Object.keys( dataConfig.properties ) ) {
                                  const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                                  if ( propertyTypeId ) {
                                      fieldItem.addProperty( propertyTypeId ).value = dataConfig.properties[ propertyTypeName ];
                                  }
                              }
                          }

                          if ( dataConfig.enumProperties ) {
                              for ( const propertyTypeName of Object.keys( dataConfig.enumProperties ) ) {
                                  const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                                  if ( propertyTypeId ) {
                                      const enumProperty = fieldItem.addEnumProperty( propertyTypeId );

                                      for ( const propertyDef of dataConfig.enumProperties[ propertyTypeName ] ) {
                                          const property = enumProperty.addProperty( propertyDef.key ?? '' );

                                          if ( propertyDef.value ) {
                                              property.value = propertyDef.value;
                                          }
                                      }
                                  }
                              }
                          }

                          if ( dataConfig.additionalData?.length ) {
                              const processAdditionalDataConfigRecursive = ( additionalDataConfig : Required<DataConfig>[ 'additionalData' ], innerParentItem : Item ) => {
                                  for ( const payload of additionalDataConfig ) {
                                      let item : Item | undefined;

                                      if ( !payload.referenceExisting ) {
                                          const itemTypeId = metaModel[ 'CFG' ].getItemType( payload.itemTypeName )?.id;

                                          item = itemTypeId
                                              ? new Item(
                                                  {
                                                      itemTypeId,
                                                      key : `${innerParentItem.key}:${payload.key}:${getUniqueIdentifierPart()}`,
                                                      name: payload.name
                                                  },
                                                  undefined,
                                                  'CFG',
                                                  true
                                              )
                                              : undefined;
                                      }
                                      else {
                                          item = payload.item
                                              ? new Item(
                                                  payload.item,
                                                  undefined,
                                                  'CFG',
                                                  true
                                              )
                                              : undefined;
                                      }

                                      if ( item ) {
                                          const referenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( payload.itemTypeName, innerParentItem.itemTypeId, payload.referenceTypeName, payload.referenceDirection )?.[ 0 ]?.id,
                                                reference      = item && referenceSetId
                                                    ? new Reference(
                                                        {
                                                            referenceSetId,
                                                            srcItemId : payload.referenceDirection === 'src'
                                                                ? item.id
                                                                : innerParentItem.id,
                                                            destItemId: payload.referenceDirection === 'dest'
                                                                ? item.id
                                                                : innerParentItem.id
                                                        },
                                                        undefined,
                                                        'CFG',
                                                        true
                                                    )
                                                    : undefined;

                                          if ( reference ) {
                                              innerParentItem.registerReference( reference );
                                              item.registerReference( reference );

                                              data.items[ item.id ] = item;
                                              data.references![ reference.id ] = reference;

                                              if ( !payload.referenceExisting ) {
                                                  if ( payload.properties ) {
                                                      for ( const propertyTypeName of Object.keys( payload.properties ) ) {
                                                          const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                                                          if ( propertyTypeId ) {
                                                              item.addProperty( propertyTypeId ).value = payload.properties[ propertyTypeName ];
                                                          }
                                                      }
                                                  }

                                                  if ( payload.enumProperties ) {
                                                      for ( const propertyTypeName of Object.keys( payload.enumProperties ) ) {
                                                          const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                                                          if ( propertyTypeId ) {
                                                              const enumProperty = item.addEnumProperty( propertyTypeId );

                                                              for ( const propertyDef of payload.enumProperties[ propertyTypeName ] ) {
                                                                  const property = enumProperty.addProperty( propertyDef.key ?? '' );

                                                                  if ( propertyDef.value ) {
                                                                      property.value = propertyDef.value;
                                                                  }
                                                              }
                                                          }
                                                      }
                                                  }
                                              }

                                              if ( payload.additionalData?.length ) {
                                                  processAdditionalDataConfigRecursive( payload.additionalData, item );
                                              }
                                          }
                                      }
                                  }
                              };

                              processAdditionalDataConfigRecursive( dataConfig.additionalData, fieldItem );
                          }

                          onNewLayoutElementDragStart?.(
                              fieldItem.id,
                              data,
                              options ?? {},
                              event
                          );
                      }
                  }
                  else {
                      return false;
                  }
              },
              [
                  dataContext,
                  dataStructure,
                  newLayoutElementsWidth,
                  onNewLayoutElementDragStart,
                  rootItem?.key
              ]
          ),
          markFieldsForAlias                         = ( alias : string | undefined ) : void => {
              setAliasToMark( alias );
          },
          setScrollPosition                          = () => {
              const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

              if ( scrollElement ) {
                  scrollElement.scrollLeft = scrollLeft;
                  scrollElement.scrollTop = scrollTop;
              }
          },
          getScrollPosition                          = () => {
              const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

              if ( scrollElement ) {
                  dispatch( setParams( uid, {
                      scrollTop : scrollElement.scrollTop ?? 0,
                      scrollLeft: scrollElement.scrollLeft ?? 0
                  } ) );
              }
          },
          debouncedGetScrollPosition                 = _.debounce( getScrollPosition.bind( null ), 250 ),
          onShowMetaFieldsCheckboxChange             = ( event : CheckboxChangeEvent ) => {
              dispatch( setParam( uid, 'showMetaFields', event.checked! ) );
          },
          onShowReferenceFieldsCheckboxChange        = ( event : CheckboxChangeEvent ) => {
              dispatch( setParam( uid, 'showReferenceFields', event.checked! ) );
          },
          onShowTypeLabelsCheckboxChange             = ( event : CheckboxChangeEvent ) => {
              dispatch( setParam( uid, 'showTypeLabels', event.checked! ) );
          },
          footer                                     = useMemo(
              () => {
                  const layoutComponents : ToolbarLayoutComponents = {
                      left : [],
                      right: []
                  };

                  layoutComponents.left.push( {
                      type: 'genericNode',

                      params: <div
                          key={`${uid}_showTypeLabelsCheckbox`}
                          className="flex align-items-center field"
                          style={{ width: '100%' }}
                      >
                          <Checkbox
                              checked={showTypeLabels}
                              onChange={onShowTypeLabelsCheckboxChange}
                              style={{ flex: '0 0 auto', width: 'auto', fontSize: '.8rem' }}
                          />
                          <label
                              htmlFor={`${uid}_showTypeLabelsCheckbox`}
                              className="block ml-3"
                              style={{ fontSize: '.8rem' }}
                          >
                              {__( 'YN.base.frontend.formDesigner.showTypeLabels.label' )}
                          </label>
                      </div>
                  } );

                  layoutComponents.left.push( {
                      type: 'genericNode',

                      params: <div
                          key={`${uid}_showReferenceFieldsCheckbox`}
                          className="flex align-items-center field mt-2"
                          style={{ width: '100%' }}
                      >
                          <Checkbox
                              checked={showReferenceFields}
                              onChange={onShowReferenceFieldsCheckboxChange}
                              style={{ flex: '0 0 auto', width: 'auto', fontSize: '.8rem' }}
                          />
                          <label
                              htmlFor={`${uid}_showReferenceFieldsCheckbox`}
                              className="block ml-3"
                              style={{ fontSize: '.8rem' }}
                          >
                              {__( 'YN.base.frontend.formDesigner.showReferenceFields.label' )}
                          </label>
                      </div>
                  } );

                  layoutComponents.left.push( {
                      type: 'genericNode',

                      params: <div
                          key={`${uid}_showMetaFieldsCheckbox`}
                          className="flex align-items-center field mt-2"
                          style={{ width: '100%' }}
                      >
                          <Checkbox
                              checked={showMetaFields}
                              onChange={onShowMetaFieldsCheckboxChange}
                              style={{ flex: '0 0 auto', width: 'auto', fontSize: '.8rem' }}
                          />
                          <label
                              htmlFor={`${uid}_showMetaFieldsCheckbox`}
                              className="block ml-3"
                              style={{ fontSize: '.8rem' }}
                          >
                              {__( 'YN.base.frontend.formDesigner.showMetaFields.label' )}
                          </label>
                      </div>
                  } );

                  if ( Object.keys( dataDescriptorAliasesInUse ).length ) {
                      layoutComponents.left.push( { type: 'separator', params: { className: 'yn-horizontal-separator' } } );
                      layoutComponents.left.push( {
                          type  : 'custom',
                          params: {
                              key      : 'hint',
                              className: 'yn-designertree-field-inuse-hint',
                              component: <>
                                  <div
                                      className="yn-designertree-field yn-designertree-field-inuse"
                                  />
                                  <span>{__( 'YN.base.frontend.formDesigner.fieldInUse.hint' )}</span>
                              </>
                          }
                      } );
                  }

                  const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

                  return <Toolbar
                      start={toolbarComponents.left}
                      className="yn-panel-toolbar shadow-4"
                      style={{ zIndex: 5 }}
                  />;
              },
              [
                  dataDescriptorAliasesInUse,
                  language,
                  messageCatalogTimestamp,
                  showMetaFields,
                  showReferenceFields,
                  showTypeLabels,
                  uid
              ]
          ),
          onContextMenuSelectionChange               = useCallback(
              ( event : TreeSelectionEvent ) => {
                  setContextMenuNode( event.value + '' );
              },
              [
                  'onlyOnce'
              ]
          ),
          onContextMenu                              = useCallback(
              ( event : TreeEventNodeEvent ) => {
                  // disable context menu on static nodes
                  if ( ( event.node as ExtendTreeNode ).dataObject ) {
                      contextMenu.current?.show( event.originalEvent );
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          contextMenuModel                           = useMemo(
              () => {
                  const retVal : MenuItem[] = [];

                  if ( contextMenuNode && dataStructure ) {
                      const dataObject = extractDataObjectsFromDataStructure( dataStructure ).find( dS => dS.id === contextMenuNode ) as DataStructure | undefined;

                      if ( dataObject ) {
                          const parentDataObject      = getParentDataObject( dataObject.id, dataStructure ),
                                dataObjectItem        = global.data[ dataContext ].items[ dataObject.origId ],
                                dataObjectItemDeleted = dataObjectItem.isDeleted(),
                                dataObjectSchema      = ( dataObject.item.objectSchema ?? dataStructure.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                                dataObjectItemType    = metaModel[ dataObjectSchema ].getItemType( dataObject.item.objectType ?? '' );

                          if ( dataObjectItemType ) {
                              const allowedReferenceSets = metaModel[ dataObjectSchema ].getReferenceSetsForItemType( dataObjectItemType.id ),
                                    definedReferenceSets = ( [] as ReferenceSet[] ).concat(
                                            parentDataObject
                                                ? getReferenceSetsForDataObject( dataObject, dataStructure, dataObjectSchema )
                                                : []
                                        )
                                        .concat(
                                            dataObject.dataStructure?.length
                                                ? dataObject.dataStructure
                                                    .reduce(
                                                        ( innerRetVal, childDataStructureToUse ) => {
                                                            innerRetVal = innerRetVal.concat( getReferenceSetsForDataObject( childDataStructureToUse, dataStructure, dataObjectSchema ) );

                                                            return innerRetVal;
                                                        },
                                                        [] as ReferenceSet[]
                                                    )
                                                : []
                                        );

                              // add button
                              if ( allowedReferenceSets.length - definedReferenceSets.length > 0 && !dataObjectItemDeleted ) {
                                  const buttonId   = `${uid}_${dataObject.id}_addButton`,
                                        menuItemId = `${dataObject.id}_addItem`;

                                  retVal.push( {
                                      id     : menuItemId,
                                      label  : __( 'YN.base.designer.tree.item.add.title' ),
                                      icon   : 'pi pi-plus-circle yn-green',
                                      data   : {
                                          buttonId
                                      },
                                      command: ( e ) => nodeButtons.current?.[ e.item.data.buttonId! ].content?.expand( new MouseEvent( 'click', { altKey: false, ctrlKey: false, shiftKey: false } ) as unknown as React.MouseEvent )
                                  } );
                              }

                              // delete/restore button
                              if ( parentDataObject ) {
                                  const buttonId = `${uid}_${dataObject.id}_deleteButton`;

                                  if ( retVal.length ) {
                                      retVal.push( { separator: true } );
                                  }

                                  retVal.push( {
                                      id     : `${dataObject.id}_${dataObjectItemDeleted ? 'undeleteItem' : 'deleteItem'}`,
                                      label  : dataObjectItemDeleted ? __( 'YN.base.designer.tree.item.undelete.title' ) : __( 'YN.base.designer.tree.item.delete.title' ),
                                      icon   : `pi ${dataObjectItemDeleted ? 'pi-plus-circle yn-dirty' : 'pi-minus-circle yn-delete'}`,
                                      data   : {
                                          buttonId
                                      },
                                      command: ( e : MenuItemCommandEvent ) => nodeButtons.current?.[ e.item.data.buttonId! ].content?.expand( e.originalEvent as unknown as React.MouseEvent )
                                  } );
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  contextMenuNode,
                  language,
                  messageCatalogTimestamp,
                  uid
              ]
          ),
          onTreeNodeToggleClick                      = useCallback(
              ( key : string ) => {
                  const dataObjectItem   = dataStructure
                            ? global.data[ dataContext ].items[ getDataObject( key, dataStructure )?.origId ?? 0 ]
                            : undefined,
                        newSelectionKeys = { ...selectionKeys };

                  if ( newSelectionKeys[ key ] ) {
                      delete newSelectionKeys[ key ];
                  }
                  else {
                      newSelectionKeys[ key ] = true
                  }

                  // deleted nodes could not be selected
                  if ( !dataObjectItem || dataObjectItem.statusId !== -1 ) {
                      dispatch( setParams( uid, {
                          selectionKeys: newSelectionKeys
                      } ) );
                  }
              },
              [
                  dataContext,
                  dataStructure,
                  selectionKeys,
                  uid
              ]
          ),
          // TODO: external config
          treeNodeTemplate                           = useCallback(
              ( treeNodeData : TreeNode.TreeNode ) : React.ReactNode => {
                  const nodeData        = treeNodeData as ExtendTreeNode,
                        classNames      = ( nodeData.className ?? '' ).split( ' ' ),
                        labelClassNames = [ 'yn-label' ];

                  let retVal : React.ReactNode = null;

                  // handle DataObject nodes
                  if ( dataStructure && nodeData.dataObject ) {
                      const dataObjectItem  = global.data[ dataContext ].items[ nodeData.dataObject.origId ],
                            formPanelErrors = allElusiveData?.[ `${parentUid}_form-${nodeData.dataObject.origId}` ]?.errorData?.count > 0;

                      let lastType : LayoutElementDefinition['type'] | undefined,
                          content : React.ReactNode = null,
                          tooltip : React.ReactNode = null;

                      // build node content if selected
                      if ( selectionKeys?.[ nodeData.key ] ) {
                          content = <div
                              key={`${nodeData.key}_body`}
                              className="yn-node-content"
                          >
                              <div
                                  id={`${nodeData.key}_scrollContainer`}
                                  className="yn-treenode-container"
                              >
                                  {nodeData.data.layoutElementDefinition.map( ( definition : LayoutElementDefinition ) => {
                                      const innerRetVal : React.ReactNode[] = [];

                                      if (
                                          (
                                              showReferenceFields
                                              || (
                                                  definition.type !== 'reference'
                                                  && definition.type !== 'referenceProperty'
                                                  && definition.type !== 'referenceImage'
                                                  && definition.type !== 'referenceMeta'
                                              )
                                          )
                                          && (
                                              showMetaFields
                                              || (
                                                  definition.type !== 'meta'
                                                  && definition.type !== 'referenceMeta'
                                              )
                                          )
                                      ) {
                                          const innerClassNames : string[] = [ 'yn-designertree-field', 'yn-fieldlike' ];

                                          if ( dataDescriptorAliasesInUse[ nodeData.dataObject?.id ?? 0 ]?.includes( definition.dataDescriptorAlias ?? '' ) ) {
                                              innerClassNames.push( 'yn-designertree-field-inuse' );
                                          }

                                          if ( definition.type === 'meta' || definition.type === 'referenceMeta' ) {
                                              innerClassNames.push( 'yn-designertree-field-meta' );
                                          }

                                          if ( definition.dataDescriptorAlias === selectedDataDescriptorAlias ) {
                                              innerClassNames.push( 'yn-designertree-field-selected' );
                                          }

                                          if ( readOnly ) {
                                              innerClassNames.push( 'p-disabled' );
                                          }

                                          if (
                                              showTypeLabels
                                              && lastType !== definition.type
                                              && definition.type !== 'meta'
                                              && definition.type !== 'referenceMeta'
                                          ) {
                                              innerRetVal.push( <p
                                                      key={definition.type}
                                                      className="yn-designertree-field-group"
                                                  >
                                                      {__( `YN.base.frontend.designer.field.type.${definition.type}.label` )}
                                                  </p>
                                              );

                                              lastType = definition.type;
                                          }

                                          innerRetVal.push( <p
                                              key={`${nodeData.key}_${definition.key}`}
                                              data-datadescriptoralias={definition.dataDescriptorAlias}
                                              draggable={!readOnly}
                                              onDoubleClick={readOnly
                                                  ? undefined
                                                  : () => {
                                                      onDoubleClick( definition.data );
                                                  }}
                                              onDragStart={readOnly
                                                  ? undefined
                                                  : ( event ) => {
                                                      onDragStart( definition.data, { label: definition.label, icon: definition.icon }, event );
                                                  }}
                                              onMouseEnter={() => {
                                                  if ( definition.dataDescriptorAlias ) {
                                                      onMarkLayoutElementsForAlias?.( definition.dataDescriptorAlias );
                                                  }
                                              }}
                                              onMouseLeave={() => {
                                                  onMarkLayoutElementsForAlias?.( undefined );
                                              }}
                                              className={innerClassNames.join( ' ' )}
                                          >
                                              {getIconSpan( definition.icon )}{definition.label}
                                          </p> );
                                      }

                                      return innerRetVal;
                                  } )}
                              </div>
                          </div>;
                      }

                      // build node tooltip
                      if ( nodeData.tooltip ) {
                          tooltip = <Tooltip target={`#${nodeData.key}`} showDelay={500} position="top">
                              {nodeData.tooltip}
                          </Tooltip>;
                      }

                      if ( selectionKeys?.[ nodeData.key ] ) {
                          if ( !classNames.includes( 'yn-designertree-node-expanded' ) ) {
                              classNames.push( 'yn-designertree-node-expanded' );
                          }
                      }
                      else {
                          const index = classNames.findIndex( cM => cM === 'yn-designertree-node-expanded' );

                          if ( index !== -1 ) {
                              classNames.splice( index, 1 );
                          }
                      }

                      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
                      if ( selectionKeys?.[ nodeData.key ] || !dataDescriptorAliasesInUse[ nodeData.dataObject?.id ?? 0 ]?.length ) {
                          const index = classNames.findIndex( cM => cM === 'yn-designertree-dataobject-inuse' );

                          if ( index !== -1 ) {
                              classNames.splice( index, 1 );
                          }
                      }
                      else if ( dataDescriptorAliasesInUse[ nodeData.dataObject?.id ?? 0 ]?.length ) {
                          if ( !classNames.includes( 'yn-designertree-dataobject-inuse' ) ) {
                              classNames.push( 'yn-designertree-dataobject-inuse' );
                          }
                      }

                      if ( dataObjectItem.isDeleted() && dataObjectItem.isModified() ) {
                          if ( !classNames.includes( 'yn-deleted' ) ) {
                              classNames.push( 'yn-deleted' );
                          }
                      }
                      else if ( dataObjectItem.isModified() ) {
                          labelClassNames.push( 'yn-dirty' );
                      }
                      else {
                          const index = classNames.findIndex( cM => cM === 'yn-deleted' );

                          if ( index !== -1 ) {
                              classNames.splice( index, 1 );
                          }
                      }

                      if ( formPanelErrors ) {
                          labelClassNames.push( 'yn-error' );
                      }

                      if ( nodeData.dataObject.origId && nodeData.dataObject.origId === selectedLayoutElementId ) {
                          if ( !classNames.includes( 'yn-designertree-node-selected' ) ) {
                              classNames.push( 'yn-designertree-node-selected' );
                          }
                      }
                      else {
                          const index = classNames.findIndex( cM => cM === 'yn-designertree-node-selected' );

                          if ( index !== -1 ) {
                              classNames.splice( index, 1 );
                          }
                      }

                      // build node
                      retVal = <>
                          {tooltip}
                          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                          <div
                              className="flex flex-column flex-auto overflow-hidden yn-designertree-header"
                              draggable="false"
                              onClick={() => {
                                  if ( !ignoreSelectionChange.current ) {
                                      onSelectLayoutElement?.( nodeData.dataObject!.origId );
                                  }

                                  ignoreSelectionChange.current = false;
                              }}
                              onMouseEnter={() => {
                                  if ( nodeData.data.dataDescriptorAlias ) {
                                      onMarkLayoutElementsForAlias?.( nodeData.data.dataDescriptorAlias );
                                  }
                              }}
                              onMouseLeave={() => {
                                  onMarkLayoutElementsForAlias?.( undefined );
                              }}
                          >
                              <div id={`${nodeData.key}`} className="yn-treenode-header">
                                  <span key="label" className={labelClassNames.join( ' ' )}>{nodeData.label ?? ''}</span>
                                  {nodeData.data.buttons}
                              </div>
                          </div>
                          {content}
                      </>;
                  }

                  // handle static nodes
                  else {
                      let content : React.ReactNode = null;

                      if ( selectionKeys?.[ nodeData.key ] ) {
                          content = <div
                              key={`${nodeData.key}_body`}
                              className="yn-node-content"
                          >
                              <div
                                  id={`${nodeData.key}_scrollContainer`}
                                  className="yn-treenode-container"
                              >
                                  {nodeData.data.layoutElementDefinition.map( ( definition : LayoutElementDefinition ) => {
                                      return <p
                                          key={`${nodeData.key}_${definition.key}`}
                                          draggable={!readOnly}
                                          onDoubleClick={readOnly
                                              ? undefined
                                              : () => {
                                                  onDoubleClick( definition.data );
                                              }}
                                          onDragStart={readOnly
                                              ? undefined
                                              : ( event ) => {
                                                  onDragStart( definition.data, { label: definition.label, icon: definition.icon }, event );
                                              }}
                                          className={`yn-fieldlike${readOnly ? ' p-disabled' : ''}`}
                                      >
                                          <span className={definition.icon}/>{definition.label}
                                      </p>;
                                  } )}
                              </div>
                          </div>;
                      }
                      retVal = <>
                          <div
                              key={`${nodeData.key}_node`}
                              className="flex flex-column flex-auto overflow-hidden yn-designertree-header"
                              draggable="false"
                          >
                              <div
                                  key={`${nodeData.key}_node_header`}
                                  id={`${nodeData.key}_node_header`}
                                  className="yn-treenode-header"
                              >
                                  <span key="label" className="yn-label">{nodeData.label ?? ''}</span>
                                  <div style={{ width: '0', float: 'right' }}></div>
                              </div>
                          </div>
                          {content}
                      </>;
                  }

                  nodeData.className = classNames.join( ' ' );

                  return retVal;
              },
              [
                  dataContext,
                  dataDescriptorAliasesInUse,
                  dataStructure,
                  language,
                  messageCatalogTimestamp,
                  onDoubleClick,
                  onDragStart,
                  onMarkLayoutElementsForAlias,
                  onSelectLayoutElement,
                  readOnly,
                  selectedDataDescriptorAlias,
                  selectedLayoutElementId,
                  selectionKeys,
                  showMetaFields,
                  showReferenceFields,
                  showTypeLabels
              ]
          ),
          treeTogglerTemplate                        = useCallback(
              ( nodeData : TreeNode.TreeNode ) : React.ReactNode => {
                  const icon = IconUtils.getJSXIcon(
                      selectionKeys?.[ nodeData.key ?? '' ]
                          ? <ChevronDownIcon/>
                          : <ChevronRightIcon/>,
                      {},
                      {}
                  );

                  return <Button
                      className={`yn-tree-toggler p-tree-toggler p-link p-unselectable-text ${nodeData.selectable === false ? 'yn-disabled' : ''}`}
                      icon={icon}
                      onClick={() => {
                          onTreeNodeToggleClick( String( nodeData.key ?? '' ) );
                      }}>
                  </Button>
              },
              [
                  onTreeNodeToggleClick,
                  selectionKeys
              ]
          ),
          content                                    = useMemo(
              () => {
                  let retVal = <></>;

                  if ( dataStructure ) {
                      retVal = <>
                          <ContextMenu
                              key={`${uid}_contextMenu`}
                              model={contextMenuModel}
                              ref={el => contextMenu.current = el}
                              onHide={() => setContextMenuNode( undefined )}
                          />
                          <Tree
                              key={`${uid}_tree`}
                              id={`${uid}_tree`}

                              ref={el => treeElement.current = el?.getElement() ?? null}

                              value={treeData}
                              nodeTemplate={treeNodeTemplate}

                              expandedKeys={expandedKeys}
                              togglerTemplate={treeTogglerTemplate}
                              // dummy on toggle needed to force PrimeReact tree to obey expanded keys property
                              onToggle={() => {
                              }}

                              selectionMode="multiple"
                              selectionKeys={selectionKeys}

                              className="yn-menu-tree yn-node-content-tree yn-designertree"
                              dragdropScope="formdesigner"

                              contextMenuSelectionKey={contextMenuNode}
                              onContextMenuSelectionChange={onContextMenuSelectionChange}
                              onContextMenu={onContextMenu}

                              footer={footer}
                              pt={{
                                  container: {
                                      onScroll: debouncedGetScrollPosition
                                  },
                                  footer   : {
                                      style: {
                                          margin: '1rem -1rem -1rem -1rem',
                                          width : 'calc(100% + 2rem)',
                                          zIndex: '10'
                                      }
                                  }
                              }}
                          />
                      </>;
                  }

                  return retVal;
              },
              [
                  contextMenuModel,
                  contextMenuNode,
                  expandedKeys,
                  footer,
                  onContextMenu,
                  onContextMenuSelectionChange,
                  rootItemId,
                  selectionKeys,
                  treeData,
                  treeNodeTemplate,
                  treeTogglerTemplate,
                  uid
              ]
          );

    lastParams.current = {
        dataContext,
        dataStructure,
        rootItem,
        globalDataVersions,
        language,
        messageCatalogTimestamp,
        metaModelTimestamp,
        onSelectLayoutElement,
        repeatedDataObjects,
        rootItemId,
        selectedLayoutElementId,
        staticTreeNodes,
        treeData,
        uid
    };

    // expose methods via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {
            markFieldsForAlias
        } )
    );

    // set scroll position (on initial render or change)
    useLayoutEffect(
        () => {
            setScrollPosition();
        },
        [
            scrollLeft,
            scrollTop,
            initialRendered.current
        ]
    );

    // mark/unmark aliasToMark
    // TODO: external config
    useLayoutEffect(
        () => {
            // remove current
            let elements = document.querySelectorAll( `.yn-menu-tree#${uid}_tree .yn-designertree-field.yn-designertree-field-using-alias` ),
                index    = 0;

            if ( elements?.length ) {
                while ( elements[ index ] ) {
                    elements[ index++ ].classList.remove( 'yn-designertree-field-using-alias' );
                }
            }

            elements = document.querySelectorAll( `.yn-menu-tree#${uid}_tree .yn-designertree-header-using-alias` );
            index = 0;

            if ( elements?.length ) {
                while ( elements[ index ] ) {
                    elements[ index++ ].classList.remove( 'yn-designertree-header-using-alias' );
                }
            }

            if ( aliasToMark ) {
                elements = document.querySelectorAll( `.yn-menu-tree#${uid}_tree .yn-designertree-field[data-datadescriptoralias="${aliasToMark}"]` );
                index = 0;

                if ( elements?.length ) {
                    while ( elements[ index ] ) {
                        elements[ index++ ].classList.add( 'yn-designertree-field-using-alias' );
                    }
                }
                else {
                    for ( const nodeKey of Object.keys( nodeKeyToDataDescriptorAliasesRepresenting.current ?? {} ) as string[] ) {
                        if ( nodeKeyToDataDescriptorAliasesRepresenting.current[ nodeKey ].includes( aliasToMark ) ) {
                            const element = document.getElementById( nodeKey );

                            //element?.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
                            element?.classList.add( 'yn-designertree-header-using-alias' );
                        }
                    }
                }
            }
        },
        [
            aliasToMark
        ]
    );

    return content;
} );
DesignerTree.defaultProps = defaultProps;
DesignerTree.displayName = 'FormDesigner-DesignerTree';


export default DesignerTree;
